d<template>
  <div id="app-layout" class="d-flex flex-column min-vh-100">
    <header class="navbar navbar-expand-lg navbar-dark fixed-top admin-header-global shadow-sm">
      <div class="container-fluid">
        <button class="navbar-toggler d-lg-none me-2" type="button" @click="toggleMenuVisibility" v-if="isDesktop">
          <span class="navbar-toggler-icon"></span>
        </button>
        <button class="btn btn-outline-light d-none d-lg-block me-2" @click="toggleMenuMode" title="Alternar modo do menu">
          <i class="bi bi-list"></i>
        </button>
        <a class="navbar-brand fw-bold tracking-wide me-2" href="#">CTech - ERP Comex</a>

        <!-- Environment Badge (só aparece em DEV/HOMOLOG) -->
        <EnvironmentBadge :release-name="releaseName" :build-time="buildTime" />

        <!-- DocCheck Badge (só aparece em DEV) -->
        <DocCheckBadge />

        <!-- Ações centrais: Helper (IA) + Sugestões e Dúvidas -->
        <div class="header-center-actions d-none d-lg-flex align-items-center gap-2">
          <button class="btn header-action-btn header-action-helper" @click="abrirNovaSugestao" title="Helper — assistente de IA">
            <i class="bi bi-robot"></i>
            <span>Helper</span>
            <span class="badge header-action-badge-ai">AI</span>
          </button>
          <button class="btn header-action-btn header-action-sugestoes" @click="abrirGestaoSugestoes" title="Sugestões de Melhorias e Dúvidas">
            <i class="bi bi-lightbulb-fill"></i>
            <span>Sugestões e Dúvidas</span>
          </button>
        </div>

        <div class="ms-auto d-flex align-items-center gap-2">
          <!-- Seletor de Empresa + Organização (exclusivo AdminInterno) -->
          <CompanySelector
            v-if="isAdminInterno"
            :lista-empresas="listaEmpresasParaSelecao"
            :empresa-atual-id="idEmpresaSelecionada"
            :lista-organizacoes="listaOrganizacoes"
            :organizacao-atual-id="idOrganizacaoSelecionada"
            :carregando-organizacoes="carregandoOrganizacoes"
            @selecionar-empresa="handleEmpresaSelecionar"
            @selecionar-organizacao="handleOrganizacaoSelecionar"
          />

          <!-- Sino de Pendências -->
          <BellPendencias @navegar-pendencia="navegarParaPendencia" />

          <!-- Avatar + Menu do Usuário -->
          <AvatarMenu
            :nome-utilizador="nomeUtilizador"
            :nome-exibicao="nomeExibicao"
            :url-foto-interna="urlFotoInterna"
            :is-admin-interno="isAdminInterno"
            :is-admin-cliente="isAdminCliente"
            :is-dark-mode="isDarkMode"
            :is-desktop="isDesktop"
            :zoom-atual="zoomPercentual"
            :zoom-opcoes="ZOOM_OPCOES"
            :release-name="releaseName"
            :build-time="buildTime"
            :lista-organizacoes="listaOrganizacoes"
            :organizacao-atual-id="idOrganizacaoSelecionada"
            @aplicar-zoom="aplicarZoom"
            @toggle-tema="toggleTheme"
            @abrir-sobre="abrirSobre"
            @alterar-senha="abrirAlterarSenha"
            @abrir-meu-perfil="abrirMeuPerfil"
            @sair="handleLogout"
            @abrir-historico-notificacoes="abrirHistoricoNotificacoes"
            @abrir-gestao-sugestoes="abrirGestaoSugestoes"
            @abrir-nova-sugestao="abrirNovaSugestao"
            @abrir-inicializacao-cliente="abrirInicializacaoCliente"
            @selecionar-organizacao="handleOrganizacaoSelecionar"
          />
        </div>
      </div>
    </header>

    <!-- Modal Sobre o Sistema -->
    <AboutModal ref="aboutModalComponent" :release-name="releaseName" :build-time="buildTime" />

    <!-- Modal Alterar Senha -->
    <AlterarSenhaModal
      v-model="showAlterarSenhaModal"
      @senha-alterada="onSenhaAlterada"
    />

    <!-- Modal Chat de Sugestões -->
    <SugestaoMelhoriaChatModal
      v-model="showSugestaoChatModal"
      :contexto="sugestaoContexto"
      @sugestoes-salvas="onSugestoesSalvas"
    />

    <!-- Modal Meu Perfil -->
    <MeuPerfilModal
      v-model="showMeuPerfilModal"
      :usuario-logado="authStore.utilizador"
      @perfil-atualizado="onPerfilAtualizado"
    />

    <div class="d-flex position-relative main-body">

      <!-- SIDEBAR PRINCIPAL (apenas desktop) -->
      <aside v-if="isDesktop" :class="['sidebar', { 'sidebar-hidden': !isMenuVisible, 'sidebar-collapsed-mode': !isMenuExpanded }]"
        :style="{'min-width': sidebarWidth, 'width': sidebarWidth, 'padding': '0 !important'}">

        <!-- Info Mobile -->
        <div class="d-lg-none p-3 border-bottom" v-if="isMenuExpanded">
          <h6 class="mb-2">Olá, {{ nomeUtilizador }}!</h6>
          <div v-if="isAdminInterno" class="mb-2">
            <label class="form-label small">Empresa:</label>
            <select class="form-select form-select-sm" :value="idEmpresaSelecionada" @change="handleEmpresaSelecionar($event.target.value ? parseInt($event.target.value, 10) : null)">
              <option :value="null">-- Minha Empresa --</option>
              <option v-for="empresa in listaEmpresasParaSelecao" :key="empresa.idEmpresa" :value="empresa.idEmpresa">{{ empresa.nomeEmpresa }}</option>
            </select>
          </div>
          <a class="nav-link text-primary small p-0" href="#" @click.prevent="handleLogout">Sair</a>
        </div>

        <!-- TOPO DO SIDEBAR: Toggle + Busca + Botão >> -->
        <div class="px-2 pb-2 border-bottom" v-if="isMenuExpanded">
          <!-- Toggle ☰ Completo / ★ Favoritos -->
          <div class="btn-group btn-group-sm w-100 mb-2" role="group">
            <button class="btn btn-outline-secondary" :class="{ 'active': modoMenu === 'completo' }"
                    @click="modoMenu = 'completo'" title="Menu Completo">
              <i class="bi bi-list"></i> Completo
            </button>
            <button class="btn btn-outline-secondary" :class="{ 'active': modoMenu === 'favoritos' }"
                    @click="modoMenu = 'favoritos'" title="Apenas Favoritos">
              <i class="bi bi-star-fill"></i> Favoritos
            </button>
          </div>

          <!-- Input de Busca Inline -->
          <div class="input-group input-group-sm">
            <span class="input-group-text bg-body-tertiary border-end-0">
              <i class="bi bi-search"></i>
            </span>
            <input type="text" class="form-control border-start-0"
                   placeholder="Buscar menu..."
                   v-model="termoBusca"
                   @keyup.enter="navegarPrimeiroResultado"
                   @keydown.escape="limparBusca"
                   @focus="aoFocarBusca"
                   @blur="aoDesfocarBusca"
                   ref="inputBusca"
                   id="sidebar-search-input">
            <button v-if="termoBusca && termoBusca.length > 0"
                    class="input-group-text bg-body-tertiary border-start-0"
                    @click="limparBusca" title="Limpar busca"
                    style="cursor: pointer;">
              <i class="bi bi-x-lg"></i>
            </button>
          </div>

          <!-- Botão >> para abrir Modal Mapa -->
          <button class="btn btn-sm btn-outline-secondary w-100 mt-2" @click="abrirModalMapa"
                  title="Mapa de Menus">
            <i class="bi bi-arrow-bar-right"></i> Mapa de Menus
          </button>
        </div>

        <!-- Sidebar colapsado: ícone de busca + mapa de menus -->
        <div class="text-center py-2" v-else>
          <button class="btn btn-sm btn-outline-secondary mb-1" @click="expandirBusca"
                  title="Buscar menu (Ctrl+K)">
            <i class="bi bi-search"></i>
          </button>
          <button class="btn btn-sm btn-outline-secondary" @click="abrirModalMapa"
                  title="Mapa de Menus">
            <i class="bi bi-compass"></i>
          </button>
        </div>

        <!-- RENDERIZAÇÃO DINÂMICA DO MENU -->
        <ul class="nav flex-column py-3 sidebar-menu-list position-relative"
            @mouseleave="closeFlyoutFromSidebar">

          <!-- MODO BUSCA: lista plana de resultados clicáveis -->
          <template v-if="termoBusca && termoBusca.length > 0">
            <!-- Modo Favoritos: filtra apenas favoritos -->
            <template v-if="modoMenu === 'favoritos'">
              <li v-for="item in menuFavoritosFlat.filter(i =>
                i.title.toLowerCase().includes(termoBusca.toLowerCase()) ||
                i.group.toLowerCase().includes(termoBusca.toLowerCase()) ||
                (i.contextoNegocio || '').toLowerCase().includes(termoBusca.toLowerCase()) ||
                (i.palavrasChave || '').toLowerCase().includes(termoBusca.toLowerCase())
              )" :key="'fav-search-' + (item.idRegraAcesso || item.module + '-' + item.title)" class="nav-item">
                <a class="nav-link search-result-link py-2"
                   :class="{ 'active': currentModule === item.module && currentModuleTitle === item.title }"
                   href="#"
                   @click.prevent="showModule(item.module, item.title, item)">
                  <i :class="['bi', item.icon]"></i>
                  <span v-if="isMenuExpanded">{{ item.title }}</span>
                  <small v-if="isMenuExpanded" class="text-muted d-block">
                    {{ item.group }}<template v-if="item.subgrupo"> › {{ item.subgrupo }}</template>
                  </small>
                </a>
              </li>
              <li v-if="menuFavoritosFlat.filter(i =>
                i.title.toLowerCase().includes(termoBusca.toLowerCase()) ||
                i.group.toLowerCase().includes(termoBusca.toLowerCase()) ||
                (i.contextoNegocio || '').toLowerCase().includes(termoBusca.toLowerCase()) ||
                (i.palavrasChave || '').toLowerCase().includes(termoBusca.toLowerCase())
              ).length === 0" class="nav-item text-center text-muted py-3">
                <small>Nenhum favorito encontrado para "{{ termoBusca }}"</small>
              </li>
            </template>
            <!-- Modo Completo: filtra todo o menu -->
            <template v-else>
              <li v-for="item in menuSearchIndex.filter(i =>
                i.title.toLowerCase().includes(termoBusca.toLowerCase()) ||
                i.group.toLowerCase().includes(termoBusca.toLowerCase()) ||
                (i.contextoNegocio || '').toLowerCase().includes(termoBusca.toLowerCase()) ||
                (i.palavrasChave || '').toLowerCase().includes(termoBusca.toLowerCase())
              )" :key="'search-' + (item.idRegraAcesso || item.module + '-' + item.title)" class="nav-item">
                <a class="nav-link search-result-link py-2"
                   :class="{ 'active': currentModule === item.module && currentModuleTitle === item.title }"
                   href="#"
                   @click.prevent="showModule(item.module, item.title, item)">
                  <i :class="['bi', item.icon]"></i>
                  <span v-if="isMenuExpanded">{{ item.title }}</span>
                  <small v-if="isMenuExpanded" class="text-muted d-block">
                    {{ item.group }}<template v-if="item.subgrupo"> › {{ item.subgrupo }}</template>
                  </small>
                </a>
              </li>
              <li v-if="menuSearchIndex.filter(i =>
                i.title.toLowerCase().includes(termoBusca.toLowerCase()) ||
                i.group.toLowerCase().includes(termoBusca.toLowerCase()) ||
                (i.contextoNegocio || '').toLowerCase().includes(termoBusca.toLowerCase()) ||
                (i.palavrasChave || '').toLowerCase().includes(termoBusca.toLowerCase())
              ).length === 0" class="nav-item text-center text-muted py-3">
                <small>Nenhum resultado encontrado para "{{ termoBusca }}"</small>
              </li>
            </template>
          </template>

          <!-- MODO FAVORITOS (sem busca): lista plana de favoritos -->
          <template v-else-if="modoMenu === 'favoritos'">
            <li v-for="item in menuFavoritosFlat" :key="'fav-' + (item.idRegraAcesso || item.module + '-' + item.title)" class="nav-item">
              <a class="nav-link search-result-link py-2"
                 :class="{ 'active': currentModule === item.module && currentModuleTitle === item.title }"
                 href="#"
                 @click.prevent="showModule(item.module, item.title, item)">
                <i :class="['bi', item.icon]"></i>
                <span v-if="isMenuExpanded">{{ item.title }}</span>
                <small v-if="isMenuExpanded" class="text-muted d-block">
                  {{ item.group }}<template v-if="item.subgrupo"> › {{ item.subgrupo }}</template>
                </small>
              </a>
            </li>
            <li v-if="menuFavoritosFlat.length === 0" class="nav-item text-center text-muted py-3">
              <small>Nenhum favorito ainda. Use o Mapa de Menus para adicionar.</small>
            </li>
          </template>

          <!-- MODO COMPLETO (sem busca): grupos com flyout -->
          <template v-else>
            <template v-for="group in menuParaExibir" :key="group.id">

              <li class="nav-item sidebar-group-item mb-2"
                  v-if="hasVisibleItems(group)"
                  @mouseenter="openFlyout(group, $event)">

                <!-- Botão do Grupo -->
                <a class="nav-link d-flex align-items-center group-link"
                   :class="{ 'flyout-open': activeFlyoutGroup && activeFlyoutGroup.id === group.id }"
                   href="#"
                   @click.prevent="toggleMobileGroup(group.id)">

                  <!-- GUARDIÃO: Retirada da classe fs-5 e de outras que engordavam a fonte -->
                  <i :class="['bi', group.icon, 'group-icon']"></i>
                  <span v-if="isMenuExpanded" class="group-text">{{ group.label }}</span>
                  <i v-if="isMenuExpanded && !isDesktop" class="bi ms-auto" :class="expandedGroupsMobile[group.id] ? 'bi-chevron-down' : 'bi-chevron-right'"></i>
                </a>

                <!-- Acordeão Mobile -->
                <div v-if="!isDesktop && expandedGroupsMobile[group.id]" class="mobile-submenu bg-body-tertiary border-y">
                  <template v-for="item in getVisibleItems(group)" :key="item.idRegraAcesso || (item.module + '-' + item.title)">
                    <!-- GUARDIÃO: Header Visual para Submenus -->
                    <div v-if="item.isHeader" class="px-4 py-2 text-uppercase text-secondary fw-bold mt-2 border-bottom" style="font-size: 0.7rem; letter-spacing: 0.05em; border-bottom-style: dashed !important;">
                      {{ item.title }}
                    </div>
                    <a v-else class="nav-link submenu-link py-2" :class="{ 'active': currentModule === item.module && currentModuleTitle === item.title, [item.colorClass]: item.colorClass }" href="#" @click.prevent="showModule(item.module, item.title, item)">
                      <i :class="['bi', item.icon]"></i> {{ item.title }}
                    </a>
                  </template>
                </div>

              </li>

            </template>
          </template>
        </ul>
      </aside>

      <!-- FLYOUT MENU SUSPENSO (DESKTOP) -->
      <div v-if="isDesktop && activeFlyoutGroup && !isFlyoutDisabled"
           ref="sidebarFlyout"
           class="sidebar-flyout shadow-lg"
           :style="{ top: flyoutTop + 'px', left: flyoutLeft + 'px' }"
           @mouseenter="clearCloseFlyoutTimer"
           @mouseleave="closeFlyoutTimer">

        <h6 class="flyout-header" v-if="!isMenuExpanded">{{ activeFlyoutGroup.label }}</h6>

        <ul class="nav flex-column mb-0">
          <template v-for="item in activeFlyoutGroup.items" :key="item.idRegraAcesso || (item.module + '-' + item.title)">
             <!-- GUARDIÃO: Header Visual para Submenus -->
             <li v-if="item.isHeader" class="px-3 py-1 text-uppercase text-secondary fw-bold mt-2 border-bottom" style="font-size: 0.7rem; letter-spacing: 0.05em; border-bottom-style: dashed !important;">
               {{ item.title }}
             </li>
              <li v-else class="nav-item flyout-item">
                <a class="nav-link flyout-link" :class="{ 'active': currentModule === item.module && currentModuleTitle === item.title, [item.colorClass]: item.colorClass }" href="#" @click.prevent="showModule(item.module, item.title, item)">
                  <i :class="['bi', item.icon, 'flyout-icon']"></i> {{ item.title }}
                </a>
             </li>
          </template>
        </ul>
      </div>

      <!-- NAVEGAÇÃO MOBILE (Bottom Tab Bar + Action Sheet) -->
      <MobileBottomNav
        v-if="!isDesktop"
        :menuConfig="menuConfig"
        @select-module="handleMobileModuleSelect"
        @abrir-mapa-menus="abrirModalMapa"
      />

      <!-- TOAST DE NOTIFICAÇÃO MOBILE (acima da tab bar) -->
      <MobileToastNotification
        v-if="!isDesktop"
        :notification="currentNotification"
        :is-blinking="isBlinking"
        @dismiss="clearNotification"
      />

      <!-- ÁREA DE CONTEÚDO PRINCIPAL -->
      <main class="flex-grow-1 content-area bg-body" :class="{ 'panel-is-open': isFormPanelOpen && currentModule, 'has-mobile-nav': !isDesktop }">
        <div v-if="activeModuleComponentObject" :class="['list-container', {'list-collapsed': isFormPanelOpen}]">
            <component
                :is="activeModuleComponentObject"
                :key="currentModule + '-' + (currentModuleExtraProps?.tipoOperacao || 'default')"
                v-bind="currentModuleExtraProps"
                @adicionar-item="handleAdicionarItem"
                @novo-item="handleAdicionarItem"
                @editar-item="handleEditarItem"
                @duplicar-item="handleDuplicarItem"
                @visualizar-item="handleVisualizarItem"
                @excluir-item="handleExcluirItem"
                @itemExcluido="handleItemExcluido"
                @confirm-action="handleConfirmAction"
                @show-notification="handleNotification"
                ref="activeModuleComponent"
            />
        </div>
        <div v-else-if="currentModule" :class="['list-container', {'list-collapsed': isFormPanelOpen}]">
            <div class="d-flex flex-column align-items-center justify-content-center h-100 text-body-secondary opacity-50">
               <i class="bi bi-cone-striped fs-1 mb-3"></i>
               <h4>Módulo de <strong>{{ currentModuleTitle }}</strong></h4>
               <p>Em construção...</p>
            </div>
        </div>
        <!-- Tela Inicial Bem Vindo -->
        <div v-else class="d-flex flex-column align-items-center justify-content-center h-100 text-body-secondary opacity-25">
             <i class="bi bi-buildings fs-1 mb-3"></i>
             <h2>CTech - ERP Comex</h2>
        </div>

        <div v-if="isFormPanelOpen" class="divider" @click="toggleFormPanel()">
            <span class="divider-text">Administrar {{ currentModuleTitle }}</span>
            <i :class="['divider-icon bi', isFormPanelOpen ? 'bi-arrow-right-short' : 'bi-arrow-left-short']"></i>
        </div>

        <div :class="['admin-panel']">
            <div class="admin-panel-body">
                <template v-if="isFormPanelOpen">
                    <MovimentacaoBancariaRelatorio
                        v-if="isRelatorioMode && currentModule === 'MovimentacoesBancariasAdministrar'"
                        :item-para-editar="itemSelecionadoParaEdicao"
                        @cancelar="closeFormPanel(true)"
                        @show-notification="handleNotification"
                    />
                    <LimitesCreditoExtrato
                        v-else-if="isRelatorioMode && currentModule === 'LimitesCreditoAdministrar'"
                        :item-para-editar="itemSelecionadoParaEdicao"
                        @cancelar="closeFormPanel(true)"
                        @show-notification="handleNotification"
                    />
                    <component
                        v-else-if="activeCadastroComponent"
                        :is="activeCadastroComponent"
                        :item-para-editar="itemSelecionadoParaEdicao"
                        v-bind="currentModuleExtraProps"
                        @item-atualizado="handleItemSalvo"
                        @cancelar="closeFormPanel(true)"
                        @changes-detected="onFormChanges"
                        @show-notification="handleNotification"
                        @solicitar-exclusao-item-lista="handleExcluirItemLista"
                    />
                </template>
            </div>
        </div>
      </main>
    </div>

    <!-- MODAL CONFIRMAÇÃO & RODAPÉ -->
    <ConfirmationModal ref="confirmationModalComponent" :title="confirmationModal.title" :message="confirmationModal.message" :confirmButtonText="confirmationModal.confirmButtonText" :cancelButtonText="confirmationModal.cancelButtonText" @confirmed="handleConfirmation" />

    <Teleport to="body">
      <footer class="bg-dark text-white text-center p-2 fixed-bottom">
        <div class="container-fluid d-flex justify-content-between align-items-center">
          <div class="dropup me-auto" ref="notificationHistoryContainer">
            <button class="btn btn-link text-secondary p-0 fs-6" type="button" id="notificationsHistoryDropdown" ref="notificationsHistoryButton" @click="toggleHistoryDropdownManually()" aria-expanded="false" aria-controls="notificationsHistoryMenu" title="Histórico de Notificações">
              <i :class="['bi', isNotificationHistoryOpen ? 'bi-caret-down-fill' : 'bi-clock-history']"></i>
            </button>
            <ul class="dropdown-menu p-2 notification-history-menu" aria-labelledby="notificationsHistoryDropdown" :class="{'show': isNotificationHistoryOpen}" :data-bs-theme="isDarkMode ? 'dark' : 'light'" id="notificationsHistoryMenu">
              <li v-if="notificationsHistory.length === 0"><span class="dropdown-item-text text-muted">Nenhuma notificação recente.</span></li>
              <li v-for="notification in notificationsHistory" :key="notification.id">
                <span class="dropdown-item-text history-item-text d-flex align-items-center">
                  <i :class="['bi', getHistoryIconClass(notification.type), 'me-2']"></i>
                  <span><strong>{{ notification.timestamp }}</strong> {{ notification.message }}</span>
                </span>
              </li>
            </ul>
          </div>
          <div class="notification-area ms-auto">
            <span v-if="currentNotification.message" :class="[notificationColorClass, {'blinking-icon': isBlinking}]" class="notification-badge" style="z-index: 2147483647;">
              <i :class="['bi', notificationIcon, 'me-2']"></i><span>{{ currentNotification.message }}</span>
            </span>
          </div>
        </div>
      </footer>
    </Teleport>

    <!-- Histórico de Notificações Mobile (slide-up) -->
    <NotificationHistoryModal
      :visible="isNotificationHistoryModalOpen"
      :notifications-history="notificationsHistory"
      @close="isNotificationHistoryModalOpen = false"
    />

    <!-- Loading Global Automático -->
    <GlobalLoadingOverlay />

    <!-- Modal Mapa de Menus -->
    <ModalMapaMenus
      v-if="isModalMapaOpen"
      :menu-config="menuConfig"
      :favoritos="favoritos"
      @fechar="fecharModalMapa"
      @show-notification="handleNotification"
      @toggle-favorito="handleToggleFavorito"
    />
  </div>
</template>

<script>
import { computed } from 'vue'
import api from '@/services/api'
import { useUiStore } from '../stores/ui.js'
import GlobalLoadingOverlay from '@/components/GlobalLoadingOverlay.vue'
import { useAuthStore } from '../stores/auth.js'
import { useTheme } from '../composables/useTheme.js'
import { useZoom } from '../composables/useZoom.js'
import { mapState, mapActions } from 'pinia'
import { Dropdown } from 'bootstrap'
import { useFavoritos } from '@/composables/useFavoritos'
import { useMenuPermissao } from '@/composables/useMenuPermissao'
import { useMenuBackend } from '@/composables/useMenuBackend'
import { useActiveScreen } from '@/composables/useActiveScreen'
import ModalMapaMenus from '@/components/navigation/ModalMapaMenus.vue'

import EmpresasAdministrar from '../components/empresasClientes/empresas/EmpresasAdministrar.vue'
import EmpresasCadastro from '../components/empresasClientes/empresas/EmpresasCadastro.vue'
import PlanosAdministrar from '../components/empresasClientes/planos/PlanosAdministrar.vue'
import PlanosCadastro from '../components/empresasClientes/planos/PlanosCadastro.vue'
import RegrasAcessoAdministrar from '../components/empresasClientes/regrasAcesso/RegrasAcessoAdministrar.vue'
import RegrasAcessoCadastro from '../components/empresasClientes/regrasAcesso/RegrasAcessoCadastro.vue'
import ListasSistemaAdministrar from '../components/listasSistema/ListasSistemaAdministrar.vue'
import ListasSistemaCadastro from '../components/listasSistema/ListasSistemaCadastro.vue'
import ParceirosAdministrar from '../components/parceiros/ParceirosAdministrar.vue'
import ParceirosCadastro from '../components/parceiros/ParceirosCadastro.vue'
import UsuariosAdministrar from '../components/usuarios/UsuariosAdministrar.vue'
import UsuariosCadastro from '../components/usuarios/UsuariosCadastro.vue'
import GruposAcessoAdministrar from '../components/empresasClientes/gruposAcesso/GruposAcessoAdministrar.vue'
import GruposAcessoCadastro from '../components/empresasClientes/gruposAcesso/GruposAcessoCadastro.vue'
import ListasClienteAdministrar from '../components/listasCliente/ListasClienteAdministrar.vue'
import ListasClienteCadastro from '../components/listasCliente/ListasClienteCadastro.vue'
import ContasBancariasAdministrar from '../components/Financeiro/cadastrosConfiguracoes/contasBancarias/ContasBancariasAdministrar.vue'
import ContasBancariasCadastro from '../components/Financeiro/cadastrosConfiguracoes/contasBancarias/ContasBancariasCadastro.vue'
import LimitesCreditoAdministrar from '../components/Financeiro/operacoesEstruturadas/limitesCredito/LimitesCreditoAdministrar.vue'
import LimitesCreditoCadastro from '../components/Financeiro/operacoesEstruturadas/limitesCredito/LimitesCreditoCadastro.vue'
import LimitesCreditoExtrato from '../components/Financeiro/operacoesEstruturadas/limitesCredito/LimitesCreditoExtrato.vue'
import FinanciamentosAdministrar from '../components/Financeiro/operacoesEstruturadas/financiamentos/FinanciamentosAdministrar.vue'
import FinanciamentosCadastro from '../components/Financeiro/operacoesEstruturadas/financiamentos/FinanciamentosCadastro.vue'
import FinanciamentoDemandaAdministrar from '../components/Financeiro/operacoesEstruturadas/financiamentos/FinanciamentoDemandaAdministrar.vue'
import ContratosHedgeAdministrar from '../components/Financeiro/operacoesEstruturadas/contratosHedge/ContratosHedgeAdministrar.vue'
import ContratosHedgeCadastro from '../components/Financeiro/operacoesEstruturadas/contratosHedge/ContratosHedgeCadastro.vue'
import ContratosCambioAdministrar from '../components/Financeiro/operacoesEstruturadas/fechamentosCambio/FechamentosCambioAdministrar.vue'
import ContratosCambioCadastro from '../components/Financeiro/operacoesEstruturadas/fechamentosCambio/FechamentosCambioCadastro.vue'
import CategoriasFinanceirasAdministrar from '../components/Financeiro/cadastrosConfiguracoes/categoriasFinanceiras/CategoriasFinanceirasAdministrar.vue'
import CategoriasFinanceirasCadastro from '../components/Financeiro/cadastrosConfiguracoes/categoriasFinanceiras/CategoriasFinanceirasCadastro.vue'
import CondicoesPagamentoAdministrar from '../components/Financeiro/cadastrosConfiguracoes/condicoesPagamento/CondicoesPagamentoAdministrar.vue'
import CondicoesPagamentoCadastro from '../components/Financeiro/cadastrosConfiguracoes/condicoesPagamento/CondicoesPagamentoCadastro.vue'
import TitulosFinanceirosAdministrar from '../components/Financeiro/contasPagarReceber/titulosFinanceiros/TitulosFinanceirosAdministrar.vue'
import TitulosFinanceirosCadastro from '../components/Financeiro/contasPagarReceber/titulosFinanceiros/TitulosFinanceirosCadastro.vue'
import PrestacaoContasAdministrar from '../components/Financeiro/contasPagarReceber/prestacaoContas/PrestacaoContasAdministrar.vue'
import PrestacaoContasCadastro from '../components/Financeiro/contasPagarReceber/prestacaoContas/PrestacaoContasCadastro.vue'
import CreditoFornecedorAdministrar from '../components/Financeiro/contasPagarReceber/prestacaoContas/CreditoFornecedorAdministrar.vue'
import SolicitacaoAdiantamentoAdministrar from '../components/Financeiro/contasPagarReceber/solicitacaoAdiantamento/SolicitacaoAdiantamentoAdministrar.vue'
import FechamentoPeriodosAdministrar from '../components/Financeiro/cadastrosConfiguracoes/fechamentoPeriodos/FechamentoPeriodosAdministrar.vue'
import FechamentoPeriodosCadastro from '../components/Financeiro/cadastrosConfiguracoes/fechamentoPeriodos/FechamentoPeriodosCadastro.vue'
import FechamentoPeriodoOperacionalAdministrar from '../components/Controladoria/fechamentoPeriodoOperacional/FechamentoPeriodoOperacionalAdministrar.vue'
import FechamentoPeriodoOperacionalCadastro from '../components/Controladoria/fechamentoPeriodoOperacional/FechamentoPeriodoOperacionalCadastro.vue'
import AtualizacaoCustoAdministrar from '../components/Controladoria/atualizacaoCusto/AtualizacaoCustoAdministrar.vue'
import AtualizacaoCustoCadastro from '../components/Controladoria/atualizacaoCusto/AtualizacaoCustoCadastro.vue'
import MovimentacoesBancariasAdministrar from '../components/Financeiro/movimentacoesBancarias/MovimentacoesBancariasAdministrar.vue'
import MovimentacoesBancariasCadastro from '../components/Financeiro/movimentacoesBancarias/MovimentacoesBancariasCadastro.vue'
import MovimentacaoBancariaRelatorio from '../components/Financeiro/movimentacoesBancarias/MovimentacaoBancariaRelatorio.vue'
import ConciliacaoBancaria from '../components/Financeiro/caixaBancos/conciliacaoBancaria/ConciliacaoBancaria.vue'
import CotacoesMoedaConsulta from '../components/Financeiro/cotacoes/CotacoesMoedaConsulta.vue'
import MoedasAdministrar from '../components/Financeiro/moedas/MoedasAdministrar.vue'
import MoedasCadastro from '../components/Financeiro/moedas/MoedasCadastro.vue'
import InicializacaoCliente from '../components/configuracoes/InicializacaoCliente.vue'
import ConfirmationModal from '../components/base/modal/ConfirmationModal.vue'
import DreLayoutAdministrar from '../components/Financeiro/relatorios/dre/DreLayoutAdministrar.vue'
import DreLayoutCadastro from '../components/Financeiro/relatorios/dre/DreLayoutCadastro.vue'
import TiposProdutoAdministrar from '../components/produtos/tiposProduto/TiposProdutoAdministrar.vue'
import TiposProdutoCadastro from '../components/produtos/tiposProduto/TiposProdutoCadastro.vue'
import ContabilistasAdministrar from '../components/organizacoes/contabilistas/ContabilistasAdministrar.vue'
import ContabilistasCadastro from '../components/organizacoes/contabilistas/ContabilistasCadastro.vue'
import SpedsAdministrar from '../components/fiscal/sped/SpedsAdministrar.vue'
import NcmAdministrar from '../components/fiscal/ncms/NcmAdministrar.vue'
import NcmCadastro from '../components/fiscal/ncms/NcmCadastro.vue'

import ProdutosAdministrar from '../components/produtos/produtos/ProdutosAdministrar.vue'
import ProdutosCadastro from '../components/produtos/produtos/ProdutosCadastro.vue'
import ProdutoFornecedorAdministrar from '../components/produtos/produtoFornecedor/ProdutoFornecedorAdministrar.vue'
import ProdutoFornecedorCadastro from '../components/produtos/produtoFornecedor/ProdutoFornecedorCadastro.vue'
import JobConfigsAdministrar from '../components/jobConfigs/JobConfigsAdministrar.vue'
import JobConfigsCadastro from '../components/jobConfigs/JobConfigsCadastro.vue'
import OrganizacaoCadastro from '../components/organizacoes/OrganizacaoCadastro.vue'
import ContratoCompraAdministrar from '../components/Compras/contratosCompra/ContratoCompraAdministrar.vue'
import ContratoCompraCadastro from '../components/Compras/contratosCompra/ContratoCompraCadastro.vue'
import PurchaseOrderAdministrar from '../components/Compras/purchaseOrders/PurchaseOrderAdministrar.vue'
import PurchaseOrderCadastro from '../components/Compras/purchaseOrders/PurchaseOrderCadastro.vue'
import OrdemCompraAdministrar from '../components/Compras/ordensCompra/OrdemCompraAdministrar.vue'
import OrdemCompraCadastro from '../components/Compras/ordensCompra/OrdemCompraCadastro.vue'
import OrdemCompraAprovacoes from '../components/Compras/ordensCompra/OrdemCompraAprovacoes.vue'
import PedidoVendaAdministrar from '../components/Vendas/pedidoVenda/PedidoVendaAdministrar.vue'

// Módulo genérico de Workflow de Aprovação
import WorkflowAprovacaoPage from '../components/base/workflow/aprovacoes/WorkflowAprovacaoPage.vue'
import { obterDefinicao } from '../components/base/workflow/aprovacoes/aprovacoesRegistry'
import '../components/Compras/ordensCompra/aprovacao/definicao' // side-effect: registra OC no registry
import '../components/Compras/purchaseOrders/aprovacao/definicao' // side-effect: registra PO no registry
import PedidoVendaCadastro from '../components/Vendas/pedidoVenda/PedidoVendaCadastro.vue'
import ContratoVendaAdministrar from '@/components/Vendas/contratosVenda/ContratoVendaAdministrar.vue'
import ContratoVendaCadastro from '@/components/Vendas/contratosVenda/ContratoVendaCadastro.vue'
import PrecosComerciaisAdministrar from '@/components/Vendas/precosComerciais/PrecosComerciaisAdministrar.vue'
import PrecosComerciaisCadastro from '@/components/Vendas/precosComerciais/PrecosComerciaisCadastro.vue'
import LoteFabricacaoAdministrar from '../components/Estoque/loteFabricacao/LoteFabricacaoAdministrar.vue'
import LoteFabricacaoCadastro from '../components/Estoque/loteFabricacao/LoteFabricacaoCadastro.vue'
import EstoqueMovimentacoes from '../components/Estoque/movimentacoes/EstoqueMovimentacoes.vue'
import EstoqueSaldosAdministrar from '../components/Estoque/saldos/EstoqueSaldosAdministrar.vue'
import EstoqueSaldosOrigemAdministrar from '../components/Estoque/saldosOrigem/EstoqueSaldosOrigemAdministrar.vue'
import EstoqueSaldosOrigemDashboard from '../components/Estoque/saldosOrigem/EstoqueSaldosOrigemDashboard.vue'
import EstoqueReservasAdministrar from '../components/Estoque/reservas/EstoqueReservasAdministrar.vue'
import EmbarquesAdministrar from '../components/logistica/embarques/EmbarquesAdministrar.vue'
import EmbarquesCadastro from '../components/logistica/embarques/EmbarquesCadastro.vue'
import LpcoAdministrar from '../components/logistica/lpco/LpcoAdministrar.vue'
import LpcoCadastro from '../components/logistica/lpco/LpcoCadastro.vue'
import EntrepostoAduaneiroAdministrar from '../components/logistica/entreposto/EntrepostoAduaneiroAdministrar.vue'
import EntrepostoAduaneiroCadastro from '../components/logistica/entreposto/EntrepostoAduaneiroCadastro.vue'
import ClausulasComerciaisAdministrar from '../components/Compras/clausulasComerciais/ClausulasComerciaisAdministrar.vue'
import ClausulasComerciaisCadastro from '../components/Compras/clausulasComerciais/ClausulasComerciaisCadastro.vue'
import OperacoesAdministrar from '../components/fiscal/operacoes/OperacoesAdministrar.vue'
import OperacoesCadastro from '../components/fiscal/operacoes/OperacoesCadastro.vue'
import PerfisTributariosAdministrar from '../components/fiscal/perfisTributarios/PerfisTributariosAdministrar.vue'
import PerfisTributariosCadastro from '../components/fiscal/perfisTributarios/PerfisTributariosCadastro.vue'
import MatrizFiscalAdministrar from '../components/fiscal/matrizFiscal/MatrizFiscalAdministrar.vue'
import MatrizFiscalCadastro from '../components/fiscal/matrizFiscal/MatrizFiscalCadastro.vue'
import TributosConfiguracaoAdministrar from '../components/fiscal/tributosConfiguracao/TributosConfiguracaoAdministrar.vue'
import TributosConfiguracaoCadastro from '../components/fiscal/tributosConfiguracao/TributosConfiguracaoCadastro.vue'
import SimuladorFiscal from '../components/fiscal/SimuladorFiscal.vue'
import DiagnosticoFiscalRelatorio from '../components/fiscal/DiagnosticoFiscalRelatorio.vue'
import MobileBottomNav from '../components/navigation/MobileBottomNav.vue'
import MobileToastNotification from '../components/navigation/MobileToastNotification.vue'

import EnvironmentBadge from '../components/layout/EnvironmentBadge.vue'
import DocCheckBadge from '../components/layout/DocCheckBadge.vue'
import BellPendencias from '@/components/layout/BellPendencias.vue'
import AvatarMenu from '../components/layout/AvatarMenu.vue'
import CompanySelector from '../components/layout/CompanySelector.vue'
import AboutModal from '../components/layout/AboutModal.vue'
import NotificationHistoryModal from '../components/layout/NotificationHistoryModal.vue'
import AlterarSenhaModal from '../components/layout/AlterarSenhaModal.vue'
import MeuPerfilModal from '../components/layout/MeuPerfilModal.vue'
import SugestaoMelhoriaChatModal from '../components/sugestoes/SugestaoMelhoriaChatModal.vue'
import SugestaoMelhoriaAdministrar from '../components/sugestoes/SugestaoMelhoriaAdministrar.vue'
import DocumentacaoTelaAdministrar from '../components/sugestoes/DocumentacaoTelaAdministrar.vue'
import DocumentacaoTelaCadastro from '../components/sugestoes/DocumentacaoTelaCadastro.vue'
import ErrorLogsAdministrar from '../components/configuracoes/ErrorLogsAdministrar.vue'
import ConfiguracoesSistemaAdministrar from '../components/configuracoes/ConfiguracoesSistemaAdministrar.vue'
import ConfiguracoesCamposSensiveisAdministrar from '../components/configuracoes/ConfiguracoesCamposSensiveisAdministrar.vue'
import RelatorioTemplateAdministrar from '../components/relatorios/RelatorioTemplateAdministrar.vue'
import IAConfiguracoes from '../components/configuracoes/ia/IAConfiguracoes.vue'

function extractEntityInfo (obj) {
  if (!obj || typeof obj !== 'object') {
    return { id: null, name: 'Item', nameLabel: 'Item' }
  }
  const keys = Object.keys(obj)
  let id = null
  let name = 'Item'
  let nameLabel = 'Item'
  const idKey = keys.find(key => key.toLowerCase().startsWith('id'))
  if (idKey) {
    id = obj[idKey]
  }

  let nameKey = keys.find(key => key.toLowerCase().includes('nome')) || keys.find(key => key.toLowerCase() === 'name')
  if (nameKey) {
    name = obj[nameKey]
    nameLabel = 'Nome'
  } else {
    nameKey = keys.find(key => key.toLowerCase().includes('descricao'))
    if (nameKey) {
      name = obj[nameKey]
      nameLabel = 'Descrição'
    } else {
      nameKey = keys.find(key => key.toLowerCase() === 'datafechada')
      if (nameKey) {
        const dataFormatada = new Date(obj[nameKey])
        name = !isNaN(dataFormatada) ? dataFormatada.toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : obj[nameKey]
        nameLabel = 'Data'
      }
    }
  }
  if (name == null || name === '') {
    name = 'Item'
  }
  return { id, name, nameLabel }
}

export default {
  name: 'MainDashboard',
  components: {
    EmpresasAdministrar,
    EmpresasCadastro,
    PlanosAdministrar,
    PlanosCadastro,
    UsuariosAdministrar,
    UsuariosCadastro,
    RegrasAcessoAdministrar,
    RegrasAcessoCadastro,
    GruposAcessoAdministrar,
    GruposAcessoCadastro,
    ListasSistemaAdministrar,
    ListasSistemaCadastro,
    ListasClienteAdministrar,
    ListasClienteCadastro,
    ParceirosAdministrar,
    ParceirosCadastro,
    CategoriasFinanceirasAdministrar,
    CategoriasFinanceirasCadastro,
    DreLayoutAdministrar,
    DreLayoutCadastro,
    CondicoesPagamentoAdministrar,
    CondicoesPagamentoCadastro,
    ContasBancariasAdministrar,
    ContasBancariasCadastro,
    LimitesCreditoAdministrar,
    LimitesCreditoCadastro,
    LimitesCreditoExtrato,
    FinanciamentosAdministrar,
    FinanciamentosCadastro,
    FinanciamentoDemandaAdministrar,
    ContratosHedgeAdministrar,
    ContratosHedgeCadastro,
    ContratosCambioAdministrar,
    ContratosCambioCadastro,
    FechamentoPeriodosAdministrar,
    FechamentoPeriodosCadastro,
    MovimentacoesBancariasAdministrar,
    MovimentacoesBancariasCadastro,
    MovimentacaoBancariaRelatorio,
    ConciliacaoBancaria,
    CotacoesMoedaConsulta,
    TitulosFinanceirosAdministrar,
    TitulosFinanceirosCadastro,
    PrestacaoContasAdministrar,
    PrestacaoContasCadastro,
    CreditoFornecedorAdministrar,
    SolicitacaoAdiantamentoAdministrar,
    InicializacaoCliente,
    ConfirmationModal,
    TiposProdutoAdministrar,
    TiposProdutoCadastro,
    ContabilistasAdministrar,
    ContabilistasCadastro,
    SpedsAdministrar,
    NcmAdministrar,
    NcmCadastro,
    ProdutosAdministrar,
    ProdutosCadastro,
    ProdutoFornecedorAdministrar,
    ProdutoFornecedorCadastro,
    JobConfigsAdministrar,
    JobConfigsCadastro,
    ClausulasComerciaisAdministrar,
    ClausulasComerciaisCadastro,
    OrganizacaoCadastro,
    ContratoCompraAdministrar,
    ContratoCompraCadastro,
    PurchaseOrderAdministrar,
    PurchaseOrderCadastro,
    OrdemCompraAdministrar,
    OrdemCompraCadastro,
    OrdemCompraAprovacoes,
    WorkflowAprovacaoPage,
    // Alias legado: menus/pendências antigos referenciam "WorkflowAprovacoes" (com "s")
    WorkflowAprovacoes: WorkflowAprovacaoPage,
    PedidoVendaAdministrar,
    PedidoVendaCadastro,
    ContratoVendaAdministrar,
    ContratoVendaCadastro,
    PrecosComerciaisAdministrar,
    PrecosComerciaisCadastro,
    LoteFabricacaoAdministrar,
    LoteFabricacaoCadastro,
    EstoqueMovimentacoes,
    EstoqueSaldosAdministrar,
    EstoqueSaldosOrigemAdministrar,
    EstoqueSaldosOrigemDashboard,
    EstoqueReservasAdministrar,
    SugestaoMelhoriaChatModal,
    SugestaoMelhoriaAdministrar,
    DocumentacaoTelaAdministrar,
    DocumentacaoTelaCadastro,
    EmbarquesAdministrar,
    EmbarquesCadastro,
    LpcoAdministrar,
    LpcoCadastro,
    EntrepostoAduaneiroAdministrar,
    EntrepostoAduaneiroCadastro,
    OperacoesAdministrar,
    OperacoesCadastro,
    PerfisTributariosAdministrar,
    PerfisTributariosCadastro,
    MatrizFiscalAdministrar,
    MatrizFiscalCadastro,
    TributosConfiguracaoAdministrar,
    TributosConfiguracaoCadastro,
    SimuladorFiscal,
    DiagnosticoFiscalRelatorio,
    FechamentoPeriodoOperacionalAdministrar,
    FechamentoPeriodoOperacionalCadastro,
    AtualizacaoCustoAdministrar,
    AtualizacaoCustoCadastro,
    MoedasAdministrar,
    MoedasCadastro,
    MobileBottomNav,
    MobileToastNotification,
    EnvironmentBadge,
    DocCheckBadge,
    BellPendencias,
    AvatarMenu,
    CompanySelector,
    AboutModal,
    NotificationHistoryModal,
    AlterarSenhaModal,
    MeuPerfilModal,
    ErrorLogsAdministrar,
    ConfiguracoesSistemaAdministrar,
    ConfiguracoesCamposSensiveisAdministrar,
    RelatorioTemplateAdministrar,
    IAConfiguracoes,
    GlobalLoadingOverlay,
    ModalMapaMenus
  },
  setup () {
    const { isDarkMode, toggleTheme, initTheme } = useTheme()
    const { zoomPercentual, aplicarZoom, initZoom, ZOOM_OPCOES } = useZoom()
    const { activeForm } = useActiveScreen()
    const releaseName = process.env.VUE_APP_RELEASE_NAME || ''
    const buildTime = process.env.VUE_APP_BUILD_TIME || ''
    return { isDarkMode, toggleTheme, initTheme, zoomPercentual, aplicarZoom, initZoom, ZOOM_OPCOES, releaseName, buildTime, activeForm }
  },
  provide () {
    return {
      isDesktop: computed(() => this.isDesktop)
    }
  },
  data () {
    return {
      authStore: useAuthStore(),
      meuPerfil: null,
      isMenuVisible: true,
      isMenuExpanded: true,
      isDesktop: window.innerWidth >= 992,
      menuConfig: [],
      activeFlyoutGroup: null,
      flyoutTop: 0,
      flyoutLeft: 0,
      flyoutTimer: null,
      expandedGroupsMobile: {},
      hasUnsavedChangesOnForm: false,
      currentNotification: {
        message: '',
        timestamp: '',
        type: 'info'
      },
      notificationsHistory: [],
      isNotificationHistoryOpen: false,
      confirmationModal: {
        title: '',
        message: '',
        confirmButtonText: 'Sim',
        cancelButtonText: 'Não',
        callback: null
      },
      isBlinking: false,
      blinkTimeout: null,
      blinkInterval: null,
      isRelatorioMode: false,
      isNotificationHistoryModalOpen: false,
      showAlterarSenhaModal: false,
      showMeuPerfilModal: false,
      showSugestaoChatModal: false,
      sugestaoContexto: {},
      currentDocId: null,

      // --- NOVOS ESTADOS: Busca, Favoritos, Toggle ---
      modoMenu: 'completo', // 'completo' | 'favoritos'
      termoBusca: '', // termo digitado na busca
      isSearchActive: false, // true quando input de busca tem foco ou texto
      isModalMapaOpen: false, // controla visibilidade do Modal Mapa
      favoritos: [], // lista de favoritos carregada via useFavoritos
      favoritosInstance: null, // instância do composable useFavoritos (evita recriação)
      menuPermissaoInstance: null, // instância do composable useMenuPermissao
      isMouseOverGroupItem: false, // flag p/ prevenir race condition no fechamento do flyout
      listaOrganizacoes: [], // organizações do tenant ativo (multi-organização)
      carregandoOrganizacoes: false
    }
  },
  computed: {
    ...mapState(useAuthStore, ['nomeUtilizador', 'isAdminInterno', 'isAdminCliente', 'idEmpresaSelecionada', 'idOrganizacaoSelecionada']),
    nomeExibicao () {
      return this.meuPerfil?.nomeExibicao || this.authStore.utilizador?.nome || ''
    },
    urlFotoInterna () {
      return this.meuPerfil?.urlFotoInterna || ''
    },
    ...mapState(useUiStore, ['currentModule', 'currentModuleTitle', 'currentModulePerms', 'currentModuleExtraProps', 'isFormPanelOpen', 'itemSelecionadoParaEdicao', 'listaEmpresasParaSelecao']),
    activeModuleComponentObject () {
      if (!this.currentModule) {
        return null
      }
      return this.$options.components[this.currentModule] || null
    },
    activeCadastroComponent () {
      if (!this.currentModule) {
        return null
      }
      const cadastroComponentName = this.currentModule.replace('Administrar', 'Cadastro')
      return this.$options.components[cadastroComponentName] || null
    },
    sidebarWidth () {
      return this.isMenuExpanded ? '240px' : '70px'
    },
    notificationIcon () {
      return this.getHistoryIconClass(this.currentNotification.type)
    },
    notificationColorClass () {
      switch (this.currentNotification.type) {
        case 'success':
          return 'notification-badge-success'
        case 'error':
          return 'notification-badge-error'
        case 'warning':
          return 'notification-badge-warning'
        case 'info':
          return 'notification-badge-info'
        default:
          return 'notification-badge-info'
      }
    },

    // --- NOVOS COMPUTEDS: Menu com Favoritos e Busca ---

    /**
     * Menu filtrado por RBAC — usa checkPerm do composable mas filtra localmente
     * para garantir reatividade com authStore.
     */
    menuComFiltroRBAC () {
      const _isAdmin = this.authStore.isAdminInterno
      const _isAdminCliente = this.authStore.isAdminCliente
      console.log('[DEBUG menuComFiltroRBAC] isAdminInterno:', _isAdmin, 'isAdminCliente:', _isAdminCliente, 'total grupos no menuConfig:', this.menuConfig?.length || 0)

      if (!this.menuConfig || this.menuConfig.length === 0) return []

      const result = []
      for (const group of this.menuConfig) {
        // Se adminOnly e não é AdminInterno, pula
        if (group.adminOnly && !_isAdmin) continue

        // Filtra itens visíveis
        const visibleItems = []
        for (const item of group.items) {
          if (item.isHeader) {
            // Header: inclui se houver itens visíveis abaixo
            const idx = group.items.indexOf(item)
            const nextHeaderIdx = group.items.findIndex((it, i) => i > idx && it.isHeader)
            const slice = nextHeaderIdx === -1
              ? group.items.slice(idx + 1)
              : group.items.slice(idx + 1, nextHeaderIdx)
            const hasVisible = slice.some(it => !it.isHeader && this.checkPerm(it.perm))
            if (hasVisible) visibleItems.push(item)
          } else if (this.checkPerm(item.perm)) {
            visibleItems.push(item)
          }
        }

        if (visibleItems.length > 0) {
          result.push({ ...group, items: visibleItems })
        }
      }

      console.log('[DEBUG menuComFiltroRBAC] grupos retornados:', result.length, result.map(g => g.label))
      return result
    },

    /**
     * Índice de busca plano (sem grupos) — construído a partir do menuComFiltroRBAC.
     */
    menuSearchIndex () {
      const _isAdmin = this.authStore.isAdminInterno
      const filtered = this.menuComFiltroRBAC
      const index = []
      for (const group of filtered) {
        let currentSubgrupo = ''
        for (const item of group.items) {
          if (item.isHeader) {
            currentSubgrupo = item.title
            continue
          }
          index.push({
            module: item.module,
            title: item.title,
            group: group.label,
            subgrupo: currentSubgrupo,
            icon: item.icon,
            perm: item.perm,
            idRegraAcesso: item.idRegraAcesso,
            idDocumentacao: item.idDocumentacao,
            contextoNegocio: item.contextoNegocio || '',
            palavrasChave: item.palavrasChave || '',
            extraProps: item.extraProps
          })
        }
      }
      console.log('[DEBUG menuSearchIndex] isAdminInterno:', _isAdmin, 'itens retornados:', index.length)
      return index
    },

    /**
     * Lista plana de favoritos (mesmo formato do menuSearchIndex).
     * Usada no modo 'favoritos' para exibir itens diretos sem grupo/flyout.
     */
    menuFavoritosFlat () {
      const favoritosValidos = (this.favoritos || []).filter(f =>
        this.menuSearchIndex.some(m => m.perm === f.chave)
      )
      return favoritosValidos.map(f => {
        const item = this.menuSearchIndex.find(m => m.perm === f.chave)
        return {
          module: item?.module || f.chave,
          title: item?.title || f.titulo || f.descricao || f.chave,
          group: item?.group || f.modulo || '',
          subgrupo: item?.subgrupo || '',
          icon: item?.icon || 'bi-star',
          perm: item?.perm || f.chave,
          idRegraAcesso: item?.idRegraAcesso || f.idRegraAcesso,
          idDocumentacao: item?.idDocumentacao,
          contextoNegocio: item?.contextoNegocio || '',
          palavrasChave: item?.palavrasChave || '',
          extraProps: item?.extraProps || {}
        }
      })
    },

    /**
     * Menu final a ser exibido no sidebar (apenas modo completo, sem busca).
     * O modo 'favoritos' é renderizado diretamente no template via menuFavoritosFlat.
     * Sem grupo "Favoritos" — favoritos aparecem apenas na aba dedicada.
     */
    menuParaExibir () {
      const result = this.menuComFiltroRBAC
      console.log('[DEBUG menuParaExibir] Modo completo, retornando', result?.length || 0, 'grupos')
      return result
    },

    /**
     * Indica se o flyout deve ser desabilitado (durante busca).
     */
    isFlyoutDisabled () {
      return this.termoBusca && this.termoBusca.length > 0
    }
  },
  methods: {
    ...mapActions(useUiStore, ['selecionarModulo', 'abrirPainelFormulario', 'fecharPainelFormulario', 'buscarEmpresasParaSelecao']),
    ...mapActions(useAuthStore, ['setEmpresaSelecionada']),
    checkPerm (permKey) {
      const result = this.menuPermissaoInstance?.checkPerm(permKey) ?? true
      // console.log('[DEBUG checkPerm] permKey:', permKey, 'result:', result, 'isAdminInterno:', this.authStore.isAdminInterno)
      return result
    },
    hasVisibleItems (group) {
      const result = this.menuPermissaoInstance?.hasVisibleItems(group) ?? false
      // console.log('[DEBUG hasVisibleItems] group:', group?.label, 'adminOnly:', group?.adminOnly, 'result:', result, 'isAdminInterno:', this.authStore.isAdminInterno)
      return result
    },
    getVisibleItems (group) {
      const result = this.menuPermissaoInstance?.getVisibleItems(group) ?? []
      // console.log('[DEBUG getVisibleItems] group:', group?.label, 'itens visiveis:', result?.length || 0)
      return result
    },
    openFlyout (group, event) {
      if (!this.isDesktop) {
        return
      }
      this.clearCloseFlyoutTimer()
      this.isMouseOverGroupItem = true
      // group.items já vem filtrado pelo menuComFiltroRBAC, usa direto
      const items = group.items || []
      if (items.length === 0) {
        this.activeFlyoutGroup = null
        return
      }
      this.activeFlyoutGroup = {
        ...group,
        items: items
      }
      const rect = event.currentTarget.getBoundingClientRect()
      this.flyoutTop = rect.top
      this.flyoutLeft = rect.right
      // Ajusta posição vertical após renderização para caber na viewport
      // (impede que o flyout fique atrás do footer — inclusive com zoom)
      this.$nextTick(() => {
        this.ajustarFlyoutParaViewport()
      })
    },
    ajustarFlyoutParaViewport () {
      const flyoutEl = this.$refs?.sidebarFlyout
      if (!flyoutEl) return
      const HEADER_H = 56
      const FOOTER_H = 40
      const MARGIN = 8
      const flyoutHeight = flyoutEl.offsetHeight
      const viewportH = window.innerHeight
      const limiteInferior = viewportH - FOOTER_H - MARGIN
      const limiteSuperior = HEADER_H + MARGIN
      // Se o flyout ultrapassa o rodapé, sobe para caber
      if (this.flyoutTop + flyoutHeight > limiteInferior) {
        this.flyoutTop = Math.max(limiteSuperior, limiteInferior - flyoutHeight)
      }
      // Garante que não fique acima do header
      if (this.flyoutTop < limiteSuperior) {
        this.flyoutTop = limiteSuperior
      }
    },
    closeFlyoutTimer () {
      if (!this.isDesktop) {
        return
      }
      this.flyoutTimer = setTimeout(() => {
        // Só fecha o flyout se o mouse NÃO estiver sobre um item de grupo
        // (previne race condition quando mouse sai do flyout e entra em grupo)
        if (this.isMouseOverGroupItem) return
        this.activeFlyoutGroup = null
      }, 350)
    },
    closeFlyoutFromSidebar () {
      // Quando o mouse sai da sidebar (<ul>), podemos resetar a flag
      // pois com certeza não está mais sobre um item de grupo
      this.isMouseOverGroupItem = false
      this.closeFlyoutTimer()
    },
    clearCloseFlyoutTimer () {
      if (this.flyoutTimer) {
        clearTimeout(this.flyoutTimer)
      }
    },
    toggleMobileGroup (groupId) {
      if (this.isDesktop) {
        return
      }
      this.expandedGroupsMobile[groupId] = !this.expandedGroupsMobile[groupId]
    },
    handleEmpresaSelecionar (id) {
      this.setEmpresaSelecionada(id)
      // Ao trocar de empresa, limpa a organização do tenant anterior e recarrega
      // as organizações do novo tenant
      this.authStore.setOrganizacaoSelecionada(null)
      this.carregarOrganizacoes()
    },
    async handleOrganizacaoSelecionar (id) {
      this.authStore.setOrganizacaoSelecionada(id)
      // Recarrega os dados do módulo ativo com a nova organização
      await this.$nextTick()
      if (this.$refs.activeModuleComponent && typeof this.$refs.activeModuleComponent.buscarDados === 'function') {
        this.$refs.activeModuleComponent.buscarDados()
      }
    },
    async carregarOrganizacoes () {
      this.carregandoOrganizacoes = true
      try {
        const response = await api.get('/Organizacoes', { _suppressLoading: true })
        this.listaOrganizacoes = Array.isArray(response.data) ? response.data : []
        // Se a organização selecionada não pertence ao tenant atual, limpa a seleção
        if (this.idOrganizacaoSelecionada && !this.listaOrganizacoes.some(o => o.idOrganizacao === this.idOrganizacaoSelecionada)) {
          this.authStore.setOrganizacaoSelecionada(null)
        }
      } catch (error) {
        console.error('[MainDashboard] Erro ao carregar organizações:', error)
        this.listaOrganizacoes = []
      } finally {
        this.carregandoOrganizacoes = false
      }
    },
    handleLogout () {
      // logout() limpa a sessão local de forma SÍNCRONA (remove authToken/
      // refreshToken do localStorage) antes de notificar o backend. Assim,
      // quando o reload abaixo descarregar a página, a guarda de navegação
      // já vê isAuthenticated === false e não reautentica o usuário.
      this.authStore.logout()
      // Força reload completo para limpar estado fantasma dos componentes
      window.location.href = '/'
    },
    abrirInicializacaoCliente () {
      this.showModule('InicializacaoCliente', 'Inicialização Cliente')
    },
    abrirSobre () {
      if (this.$refs.aboutModalComponent) {
        this.$refs.aboutModalComponent.show()
      }
    },
    abrirAlterarSenha () {
      this.showAlterarSenhaModal = true
    },
    abrirMeuPerfil () {
      this.showMeuPerfilModal = true
    },
    onPerfilAtualizado () {
      this.showNotification('Perfil atualizado com sucesso!', 'success')
      this.carregarMeuPerfil()
      // Recarrega o token para atualizar claims se necessário
      this.authStore.refreshAccessToken().catch(() => {})
    },
    async carregarMeuPerfil () {
      try {
        const id = this.authStore.utilizador?.id
        if (!id) return
        const response = await api.get(`/Usuarios/${id}`)
        this.meuPerfil = response.data
      } catch (error) {
        console.error('[MainDashboard] Erro ao carregar perfil:', error)
      }
    },
    abrirGestaoSugestoes () {
      this.sugestaoContexto = {
        modulo: this.currentModule || '',
        tela: this.currentModuleTitle || '',
        rota: this.$route?.path || '',
        versaoSistema: process.env.VUE_APP_VERSION || ''
      }
      this.showModule('SugestaoMelhoriaAdministrar', 'Sugestões de Melhorias')
    },
    abrirNovaSugestao () {
      // Usa o useActiveScreen para identificar o formulário real
      const { activeForm } = useActiveScreen()
      const formAtivo = activeForm.value

      // Constrói contextoExtra com dados reais do formulário ativo
      let contextoExtra = ''
      if (this.isFormPanelOpen && this.itemSelecionadoParaEdicao) {
        const item = this.itemSelecionadoParaEdicao
        const dados = { id: item.id || '---' }
        // Extrai campos relevantes do snapshot (evita enviar objetos complexos)
        for (const key of Object.keys(item)) {
          if (typeof item[key] === 'string' || typeof item[key] === 'number' || typeof item[key] === 'boolean') {
            dados[key] = item[key]
          }
        }
        contextoExtra = JSON.stringify({
          form: formAtivo || this.currentModuleTitle || '',
          registroId: item.id || '---',
          dados
        })
      }
      // Capturar modais abertos no DOM (cobre ModalFormulario v1 e v2)
      try {
        const modais = document.querySelectorAll('[role="dialog"][aria-modal="true"]')
        const titulos = []
        modais.forEach(function (m) {
          const t = m.querySelector('.modal-title, .modal-title-v2, .modal-header h5, .modal-header h6, .modal-header-v2 h5')
          if (t && t.textContent.trim()) titulos.push(t.textContent.trim())
        })
        console.log('[Sugestoes] Modais encontrados:', modais.length, 'Titulos:', titulos)
        if (titulos.length > 0) {
          contextoExtra = (contextoExtra ? contextoExtra + ' | ' : '') + 'Modal: ' + titulos.join(', ')
        } else if (modais.length > 0) {
          contextoExtra = (contextoExtra ? contextoExtra + ' | ' : '') + 'Modal(s) aberto(s) sem titulo capturado'
        }
      } catch (e) {
        console.warn('[Sugestoes] Erro captura modais:', e)
      }
      console.log('[Sugestoes] Contexto:', { modulo: this.currentModule, tela: this.currentModuleTitle, contextoExtra, idDocumentacao: this.currentDocId })
      this.sugestaoContexto = {
        // [!] F4: Quando form panel está aberto, usa activeForm como módulo
        // para garantir que o IdDocumentacao correto seja enviado ao helper.
        modulo: (this.isFormPanelOpen && this.activeForm)
          ? this.activeForm
          : (this.currentModule || ''),
        tela: this.currentModuleTitle || '',
        rota: this.$route?.path || '',
        versaoSistema: process.env.VUE_APP_VERSION || '',
        nomeUsuario: this.nomeUtilizador || '',
        contextoExtra: contextoExtra,
        idDocumentacao: (this.isFormPanelOpen && this.activeForm)
          ? this.activeForm
          : (this.currentDocId || ''),
        idUsuario: this.authStore.utilizador?.id || null
      }
      this.showSugestaoChatModal = true
    },
    onSugestoesSalvas () {
      // Não altera a página atual — o chat modal já abre uma nova aba
      // com a gestão de sugestões via sessionStorage + window.open
    },
    onSenhaAlterada () {
      this.showNotification('Senha alterada com sucesso!', 'success')
    },
    clearNotificationTimers () {
      clearTimeout(this.blinkTimeout)
      clearInterval(this.blinkInterval)
    },
    clearNotification () {
      this.clearNotificationTimers()
      this.currentNotification = { message: '', type: 'info' }
      this.isBlinking = false
    },
    startNotificationAnimation () {
      this.clearNotificationTimers()
      this.isBlinking = false
      this.blinkTimeout = setTimeout(() => {
        let blinkCount = 0
        this.blinkInterval = setInterval(() => {
          this.isBlinking = !this.isBlinking
          blinkCount++
          if (blinkCount >= 6) {
            clearInterval(this.blinkInterval)
            this.isBlinking = false
          }
        }, 130)
      }, 1000)
      setTimeout(this.clearNotification, 7000)
    },
    showNotification (message, type = 'info') {
      const now = new Date()
      const newNotification = {
        message,
        type,
        timestamp: `(${now.toLocaleTimeString('pt-BR')})`,
        id: Date.now()
      }
      this.currentNotification = newNotification
      this.notificationsHistory.unshift(newNotification)
      if (this.notificationsHistory.length > 50) {
        this.notificationsHistory.pop()
      }
      this.startNotificationAnimation()
    },
    getHistoryIconClass (type) {
      switch (type) {
        case 'success':
          return 'bi-check-circle text-success'
        case 'error':
          return 'bi-x-circle text-danger'
        case 'warning':
          return 'bi-exclamation-triangle text-warning'
        default:
          return 'bi-info-circle text-info'
      }
    },
    async showModule (moduleName, moduleTitle, itemConfig) {
      const extraProps = itemConfig?.extraProps || {}
      // Se for o mesmo módulo com mesmo título e mesmas extraProps, ignora
      if (this.currentModule === moduleName &&
          this.currentModuleTitle === moduleTitle &&
          JSON.stringify(this.currentModuleExtraProps) === JSON.stringify(extraProps)) {
        return
      }
      const perms = itemConfig?.perm
        ? this.authStore.getPermsDoModulo(itemConfig.perm)
        : []
      this.currentDocId = moduleName || null
      if (this.isFormPanelOpen && this.hasUnsavedChangesOnForm) {
        this.showConfirmationModal('Descartar Alterações?', 'Você tem alterações não salvas. Deseja descartá-las?', (confirmed) => {
          if (confirmed) {
            this.selecionarModulo(moduleName, moduleTitle, perms, extraProps)
            this.hasUnsavedChangesOnForm = false
            if (window.innerWidth < 992) {
              this.isMenuVisible = false
            }
          }
        })
      } else {
        this.selecionarModulo(moduleName, moduleTitle, perms, extraProps)
        if (window.innerWidth < 992) {
          this.isMenuVisible = false
        }
      }
    },
    async navegarParaPendencia (destino) {
      if (!destino || !destino.modulo) return
      const extraProps = destino.chave ? { chave: destino.chave } : {}
      this.showModule(destino.modulo, destino.titulo || destino.modulo, {
        idDocumentacao: destino.idDocumentacao,
        extraProps
      })
      await this.abrirItemPendencia(destino)
    },
    async abrirItemPendencia (destino) {
      await this.$nextTick()
      await this.$nextTick()

      // Pendência de Sugestão de Melhoria
      if (destino.modulo === 'SugestaoMelhoriaAdministrar' && destino.id) {
        if (this.$refs.activeModuleComponent && typeof this.$refs.activeModuleComponent.abrirSugestaoDesdeWidget === 'function') {
          this.$refs.activeModuleComponent.abrirSugestaoDesdeWidget({ idSugestaoMelhoria: destino.id })
        }
        return
      }

      // Resolve entityKey a partir da chave (se houver)
      let entityKey = 'idOrdemCompra'
      let entityId = destino.idOrdemCompra
      if (destino.chave) {
        try {
          const def = obterDefinicao(destino.chave)
          entityKey = def.entityKey
          entityId = destino[entityKey]
        } catch (e) {
          // fallback: mantém idOrdemCompra para compatibilidade legado
          entityKey = 'idOrdemCompra'
          entityId = destino.idOrdemCompra
        }
      }

      if (!entityId) return

      if (destino.acao === 'analisar') {
        if (this.$refs.activeModuleComponent && typeof this.$refs.activeModuleComponent.abrirModalAnalise === 'function') {
          this.$refs.activeModuleComponent.abrirModalAnalise({ [entityKey]: entityId })
        }
        return
      }

      // Ação editar (fallback): busca via API
      try {
        const { data } = await api.get(`/${entityKey === 'idOrdemCompra' ? 'OrdensCompra' : entityKey.replace('id', '')}/${entityId}`)
        this.handleEditarItem(data)
      } catch (e) {
        this.showNotification('Erro ao carregar item.', 'error')
      }
    },
    handleAdicionarItem (item) {
      // Hub "Demanda de Financiamento": navega para o CRUD de Financiamentos
      if (this.currentModule === 'FinanciamentoDemandaAdministrar') {
        const prefill = item && item.idPedidoVenda ? { idPedidoVenda: item.idPedidoVenda } : null
        this.showModule('FinanciamentosAdministrar', 'Nova Operação de Financiamento')
        this.$nextTick(() => {
          this.abrirPainelFormulario(prefill)
        })
        return
      }
      this.abrirPainelFormulario()
      this.hasUnsavedChangesOnForm = false
      document.title = this.currentModuleTitle
        ? `CTech - Novo ${this.currentModuleTitle}`
        : 'CTech'
    },
    async handleEditarItem (item) {
      this.isRelatorioMode = false

      // Hub "Demanda de Financiamento": navega para o CRUD de Financiamentos
      if (this.currentModule === 'FinanciamentoDemandaAdministrar') {
        if (item && item._isDemandaVerPedido) {
          // Ver pedido: navega para PedidoVenda em modo leitura
          this.showModule('PedidoVendaAdministrar', 'Pedido de Venda')
          this.$nextTick(() => {
            this.abrirPainelFormulario({ idPedidoVenda: item.idPedidoVenda })
          })
          return
        }
        // Editar/Ver financiamento: navega para FinanciamentosCadastro
        this.showModule('FinanciamentosAdministrar', 'Operação de Financiamento')
        this.$nextTick(() => {
          this.abrirPainelFormulario(item || null)
        })
        return
      }

      // Se veio da solicitação com flag de criar título ADT, redireciona para o cadastro de títulos
      if (item && item._criarTitulo) {
        delete item._criarTitulo

        // Busca a categoria financeira para TransitoAdiantamentoFornecedor
        let categoriaAdt = null
        try {
          const response = await api.get('/CategoriasFinanceiras/resolver-por-parametro', {
            params: { parametro: 'CONFIG_DEFINE_CATEG_FIN22' }
          })
          categoriaAdt = response.data
        } catch (e) {
          this.showNotification('Erro ao resolver categoria financeira para adiantamento. Verifique a configuração do plano de categorias.', 'error')
          return
        }

        // Monta os TituloFinanceiroItem a partir dos itens da solicitação
        const itensCategoria = (item.itens || []).map(i => ({
          idCategoriaFinanceira: categoriaAdt.idCategoriaFinanceira,
          nomeCategoriaFinanceira: categoriaAdt.nomeCategoria,
          nomeExibicaoCategoriaFinanceira: categoriaAdt.nomeExibicao,
          codigoEstruturado: categoriaAdt.codigoEstruturado,
          valorMoedaTitulo: i.valor,
          valorMoedaPadrao: 0,
          valorMoedaGerencial: null,
          descricaoComplementar: i.descricao
        }))

        const tituloPrefill = {
          ...item,
          idTituloFinanceiro: 0,
          isAdiantamento: true,
          idSolicitacaoAdiantamento: item.idSolicitacaoAdiantamento,
          observacaoLivre: item.observacao,
          itens: itensCategoria
        }
        this.showModule('TitulosFinanceirosAdministrar', 'Criar Título ADT a Pagar')
        this.$nextTick(() => {
          this.abrirPainelFormulario(tituloPrefill)
        })
        return
      }

      // Se veio da tela de Créditos por Fornecedor com flag de nova prestação,
      // redireciona para o cadastro de Prestação de Contas
      if (item && item.isNovaPrestacao) {
        this.showModule('PrestacaoContasAdministrar', 'Nova Prestação de Contas')
        this.$nextTick(() => {
          this.abrirPainelFormulario(item)
        })
        return
      }

      this.abrirPainelFormulario(item)
      this.hasUnsavedChangesOnForm = false
      const { name } = extractEntityInfo(item)
      document.title = this.currentModuleTitle
        ? `CTech - Editando ${this.currentModuleTitle}: ${name}`
        : 'CTech'
    },
    handleDuplicarItem (item) {
      const clone = JSON.parse(JSON.stringify(item))

      // Zera a chave primária (primeira chave 'id*', exceto idEmpresa/idTenant)
      const pkKey = Object.keys(clone).find(k => {
        const lk = k.toLowerCase()
        return lk.startsWith('id') && lk !== 'idempresa' && lk !== 'idtenant'
      })
      if (pkKey) clone[pkKey] = null

      // Remove campos de auditoria para não exibir dados do registro original
      ;['tsCriacao', 'tsModificacao', 'nomeUsuarioCriacao', 'nomeUsuarioModificacao']
        .forEach(k => delete clone[k])

      // Sufixa a descrição (respeitando MaxLength(100)) para evitar colisão imediata
      if (clone.descricao) {
        clone.descricao = `${clone.descricao.slice(0, 92)} (Cópia)`
      }

      // Flag lida pelo handleItemSalvo para notificar "cadastrado" em vez de "atualizado"
      clone._isDuplicacao = true

      this.isRelatorioMode = false
      this.abrirPainelFormulario(clone)
      this.hasUnsavedChangesOnForm = false
      document.title = this.currentModuleTitle
        ? `CTech - Duplicar ${this.currentModuleTitle}`
        : 'CTech'
    },
    handleVisualizarItem (item) {
      // Se o item tiver um module definido, navega para ele (drill-down)
      if (item && item.module) {
        this.showModule(item.module, item.title || item.module)
        return
      }
      this.isRelatorioMode = true
      this.abrirPainelFormulario(item)
      this.hasUnsavedChangesOnForm = false
      const { name } = extractEntityInfo(item)
      document.title = this.currentModuleTitle
        ? `CTech - Visualizando ${this.currentModuleTitle}: ${name}`
        : 'CTech'
    },
    async closeFormPanel (reloadList = true) {
      console.log('[DEBUG MainDashboard] closeFormPanel chamado! reloadList:', reloadList, 'currentModule:', this.currentModule)
      const isCriarTituloADT = this.currentModuleTitle?.includes('Criar Título ADT')
      const isNovaPrestacao = this.currentModuleTitle?.includes('Nova Prestação de Contas')
      this.isRelatorioMode = false
      this.fecharPainelFormulario()
      this.hasUnsavedChangesOnForm = false
      // Restaura o título da aba para o nome do módulo
      document.title = this.currentModuleTitle
        ? `CTech - ${this.currentModuleTitle}`
        : 'CTech'

      // Se estava criando título ADT, volta para a tela de Solicitações de Adiantamento
      if (isCriarTituloADT) {
        this.selecionarModulo('SolicitacaoAdiantamentoAdministrar', 'Solicitações de Adiantamento', [])
        await this.$nextTick()
        if (this.$refs.activeModuleComponent && typeof this.$refs.activeModuleComponent.buscarDados === 'function') {
          this.$refs.activeModuleComponent.buscarDados()
        }
        return
      }

      // Se estava criando prestação de contas, volta para a tela de Créditos por Fornecedor
      if (isNovaPrestacao) {
        this.selecionarModulo('CreditoFornecedorAdministrar', 'Créditos por Fornecedor', [])
        await this.$nextTick()
        if (this.$refs.activeModuleComponent && typeof this.$refs.activeModuleComponent.buscarDados === 'function') {
          this.$refs.activeModuleComponent.buscarDados()
        }
        return
      }

      if (reloadList && this.currentModule) {
        await this.$nextTick()
        if (this.$refs.activeModuleComponent && typeof this.$refs.activeModuleComponent.buscarDados === 'function') {
          this.$refs.activeModuleComponent.buscarDados()
        }
      }
    },
    async handleItemSalvo (savedItem) {
      const { id, name } = extractEntityInfo(savedItem)
      const isNew = this.itemSelecionadoParaEdicao === null ||
        this.itemSelecionadoParaEdicao?._isDuplicacao === true
      const acao = isNew ? 'cadastrado' : 'atualizado'
      const tipoItem = this.currentModuleTitle.slice(0, -1) || 'Item'
      const idInfo = id ? ` (ID: ${id})` : ''
      const message = `${tipoItem} "${name}"${idInfo} ${acao} com sucesso!`
      this.showNotification(message, 'success')

      // Se o título salvo tem idSolicitacaoAdiantamento, aprova a solicitação
      if (savedItem && savedItem.idSolicitacaoAdiantamento) {
        try {
          await api.put(`/SolicitacoesAdiantamento/${savedItem.idSolicitacaoAdiantamento}/aprovar`)
        } catch (e) {
          console.warn('Erro ao aprovar solicitação de adiantamento:', e)
        }
      }

      this.closeFormPanel(true)
    },
    toggleMenuVisibility () {
      this.isMenuVisible = !this.isMenuVisible
    },
    toggleMenuMode () {
      if (window.innerWidth >= 992) {
        this.isMenuExpanded = !this.isMenuExpanded
      }
    },
    handleResize () {
      this.isDesktop = window.innerWidth >= 992
      if (this.isDesktop) {
        this.isMenuVisible = true
      } else {
        this.isMenuVisible = false
        this.isMenuExpanded = true
      }
    },
    handleMobileModuleSelect ({ module, title, item }) {
      const perms = item?.perm
        ? this.authStore.getPermsDoModulo(item.perm)
        : []
      const extraProps = item?.extraProps || {}
      this.currentDocId = module || null
      if (this.isFormPanelOpen && this.hasUnsavedChangesOnForm) {
        this.showConfirmationModal('Descartar Alterações?', 'Você tem alterações não salvas. Deseja descartá-las?', (confirmed) => {
          if (confirmed) {
            this.selecionarModulo(module, title, perms, extraProps)
            this.hasUnsavedChangesOnForm = false
          }
        })
      } else {
        this.selecionarModulo(module, title, perms, extraProps)
      }
    },
    handleConfirmAction (payload) {
      this.showConfirmationModal(payload.title, payload.message, payload.callback, payload.confirmText, payload.cancelText)
    },
    showConfirmationModal (title, message, callback, confirmText = 'Sim', cancelText = 'Não') {
      if (this.$refs.confirmationModalComponent) {
        this.confirmationModal.title = title
        this.confirmationModal.message = message
        this.confirmationModal.confirmButtonText = confirmText
        this.confirmationModal.cancelButtonText = cancelText
        this.confirmationModal.callback = callback
        this.$refs.confirmationModalComponent.show()
      }
    },
    handleConfirmation (result) {
      if (this.confirmationModal.callback) {
        this.confirmationModal.callback(result)
        this.confirmationModal.callback = null
      }
    },
    handleExcluirItem (item) {
      const { id, name } = extractEntityInfo(item)
      this.showConfirmationModal(
        'Confirmar Exclusão',
        `Tem a certeza que deseja excluir "${name}"?`,
        async (confirmed) => {
          if (confirmed) {
            try {
              let apiPath = this.currentModule.replace('Administrar', '')
              if (apiPath === 'Usuarios') apiPath = 'Usuarios'
              if (apiPath === 'RegrasAcesso') apiPath = 'RegrasAcesso'
              if (apiPath === 'GruposAcesso') apiPath = 'GruposAcesso'
              if (apiPath === 'ListasCliente') apiPath = 'ListasCliente'
              if (apiPath === 'ListasSistema') apiPath = 'ListasSistema'
              if (apiPath === 'Parceiros') apiPath = 'Parceiros'
              if (apiPath === 'Organizacao') apiPath = 'Organizacao'
              if (apiPath === 'ContasBancarias') apiPath = 'ContasBancarias'
              if (apiPath === 'MovimentacoesBancarias') apiPath = 'MovimentacoesBancarias'
              if (apiPath === 'FechamentoPeriodos') apiPath = 'FechamentoPeriodos'
              if (apiPath === 'TiposProduto') apiPath = 'TiposProduto'
              if (apiPath === 'Ncm') apiPath = 'Ncms'
              if (apiPath === 'Lc116') apiPath = 'Lc116'
              if (apiPath === 'Nbs') apiPath = 'Nbs'
              if (apiPath === 'Produto') apiPath = 'Produtos'
              if (apiPath === 'Embarque') apiPath = 'Embarque'
              if (apiPath === 'ProdutoFornecedor') apiPath = 'ProdutoFornecedores'
              if (apiPath === 'LoteFabricacao') apiPath = 'LoteFabricacao'
              if (apiPath === 'CondicoesPagamento') apiPath = 'CondicoesPagamento'
              if (apiPath === 'LimitesCredito') apiPath = 'LimiteCredito'
              if (apiPath === 'Financiamentos') apiPath = 'Financiamento'
              if (apiPath === 'ContratosHedge') apiPath = 'OperacaoHedge'
              if (apiPath === 'TitulosFinanceiros') apiPath = 'TituloFinanceiro'
              if (apiPath === 'PrestacaoContas') apiPath = 'PrestacaoContas'
              if (apiPath === 'FechamentoPeriodoOperacional') apiPath = 'FechamentosPeriodosOperacionais'
              if (apiPath === 'SimuladorFiscal') return

              await api.delete(`/${apiPath}/${id}`)
              this.showNotification(`"${name}" excluído com sucesso!`, 'success')
              this.closeFormPanel(true)
            } catch (error) {
              console.error('Erro ao excluir item:', error)
              this.showNotification(`Erro ao excluir "${name}". ${error.response?.data?.message || ''}`, 'error')
            }
          }
        }
      )
    },
    handleExcluirItemLista (item) {
      if (!item) {
        return
      }
      this.showConfirmationModal('Confirmar Exclusão', `Tem a certeza de que deseja excluir o item "${item.textoLista}"?`, async (confirmed) => {
        if (!confirmed) {
          return
        }
        const isSistema = this.currentModule.includes('Sistema')
        const endpoint = isSistema ? 'ListasSistema' : 'ListasCliente'
        try {
          await api.delete(`/${endpoint}/itens/${item.idSequenciaTabela}`)
          this.showNotification('Item da lista excluído com sucesso.', 'success')
          if (this.$refs.activeModuleComponent && typeof this.$refs.activeModuleComponent.buscarItens === 'function') {
            this.$refs.activeModuleComponent.buscarItens()
          } else {
            this.closeFormPanel(true)
          }
        } catch (error) {
          this.showNotification(error.response?.data?.message || 'Erro ao excluir o item.', 'error')
        }
      })
    },
    onFormChanges (hasChanges) {
      this.hasUnsavedChangesOnForm = hasChanges
    },
    handleNotification (payload) {
      console.log('[DEBUG MainDashboard] handleNotification recebido:', JSON.stringify(payload))
      this.showNotification(payload.message, payload.type || 'info')
    },
    toggleHistoryDropdownManually () {
      const dropdownElement = this.$refs.notificationsHistoryButton
      if (dropdownElement) {
        const bsDropdown = Dropdown.getInstance(dropdownElement) || new Dropdown(dropdownElement)
        bsDropdown.toggle()
      }
    },
    abrirHistoricoNotificacoes () {
      this.isNotificationHistoryModalOpen = true
    },
    handleClickOutside (event) {
      const container = this.$refs.notificationHistoryContainer
      if (container && !container.contains(event.target) && this.isNotificationHistoryOpen) {
        this.isNotificationHistoryOpen = false
      }
    },
    toggleFormPanel () {
      if (this.isFormPanelOpen && this.hasUnsavedChangesOnForm) {
        this.showConfirmationModal('Descartar Alterações?', 'Você tem alterações não salvas. Deseja descartá-las?', (confirmed) => {
          if (confirmed) {
            this.fecharPainelFormulario()
            this.hasUnsavedChangesOnForm = false
          }
        })
      } else {
        if (this.isFormPanelOpen) {
          this.fecharPainelFormulario()
        }
      }
    },

    // --- NOVOS MÉTODOS: Busca e Favoritos ---

    /**
     * Carrega favoritos do backend usando o composable useFavoritos.
     */
    async carregarFavoritos () {
      if (this.favoritosInstance) {
        // carregarFavoritos() agora retorna os dados diretamente,
        // evitando problema de desembrulho de Ref fora do setup()
        const dados = await this.favoritosInstance.carregarFavoritos()
        console.log('[MainDashboard carregarFavoritos] dados retornados:', JSON.stringify(dados), '| length:', dados?.length)
        this.favoritos = dados || []
      }
    },

    /**
     * Navega para o primeiro resultado da busca.
     * No modo favoritos, busca apenas nos favoritos.
     */
    navegarPrimeiroResultado () {
      const fonte = this.modoMenu === 'favoritos' ? this.menuFavoritosFlat : this.menuSearchIndex
      const resultados = fonte.filter(item => {
        const termo = this.termoBusca.toLowerCase()
        return item.title.toLowerCase().includes(termo) ||
               item.group.toLowerCase().includes(termo) ||
               (item.contextoNegocio || '').toLowerCase().includes(termo) ||
               (item.palavrasChave || '').toLowerCase().includes(termo)
      })
      if (resultados.length > 0) {
        const item = resultados[0]
        this.showModule(item.module, item.title, item)
        this.limparBusca()
      }
    },

    /**
     * Recebe toggle de favoritos do ModalMapaMenus e persiste no backend.
     * O payload já vem no formato { favoritos: [{ idRegraAcesso, ordem }] }
     */
    async handleToggleFavorito (payload) {
      if (!this.favoritosInstance) return
      try {
        // Preserva os dados de exibição (chave/descricao/modulo) mesclando com
        // os favoritos já carregados (do GET) e com o menuSearchIndex (do menu da API).
        // Sem isso, 'chave' viria undefined e o menuComFavoritos deixaria de casar.
        const antigos = this.favoritosInstance.favoritos || []
        const novos = payload.favoritos.map((f, idx) => {
          const antigo = antigos.find(a => Number(a.idRegraAcesso) === Number(f.idRegraAcesso))
          const menuItem = this.menuSearchIndex.find(m => Number(m.idRegraAcesso) === Number(f.idRegraAcesso))
          return {
            idRegraAcesso: f.idRegraAcesso,
            ordem: f.ordem || idx + 1,
            chave: antigo?.chave || menuItem?.perm || '',
            descricao: antigo?.descricao || menuItem?.title || '',
            modulo: antigo?.modulo || menuItem?.group || ''
          }
        })

        await this.favoritosInstance.salvarFavoritos(novos)
        // Atualiza a referência local
        this.favoritos = this.favoritosInstance.favoritos || []
      } catch (e) {
        console.error('[MainDashboard] Erro ao salvar favoritos:', e)
        this.showNotification('Erro ao salvar favoritos.', 'error')
      }
    },

    /**
     * Limpa o termo de busca e restaura o menu normal.
     */
    limparBusca () {
      this.termoBusca = ''
      this.isSearchActive = false
    },

    /**
     * Ao focar no input de busca, marca como ativo.
     */
    aoFocarBusca () {
      this.isSearchActive = true
      // Se sidebar estiver colapsado, expande
      if (!this.isMenuExpanded) {
        this.isMenuExpanded = true
      }
    },

    /**
     * Ao desfocar, se o input estiver vazio, desmarca.
     */
    aoDesfocarBusca () {
      if (!this.termoBusca || this.termoBusca.length === 0) {
        this.isSearchActive = false
      }
    },

    /**
     * Expande o sidebar e foca no input de busca.
     */
    expandirBusca () {
      this.isMenuExpanded = true
      this.$nextTick(() => {
        if (this.$refs.inputBusca) {
          this.$refs.inputBusca.focus()
        }
      })
    },

    /**
     * Abre o Modal Mapa de Menus.
     */
    abrirModalMapa () {
      this.isModalMapaOpen = true
    },

    /**
     * Fecha o Modal Mapa de Menus.
     */
    fecharModalMapa () {
      this.isModalMapaOpen = false
    }
  },
  created () {
    this.menuPermissaoInstance = useMenuPermissao(this.menuConfig)
    console.log('[DEBUG created] menuPermissaoInstance setada, menuConfig grupos:', this.menuConfig?.length || 0)
  },
  async mounted () {
    this.initTheme()
    this.initZoom()
    this.carregarMeuPerfil()
    // Remove o style anti-flash injetado no index.html agora que o tema
    // oficial do Bootstrap já foi aplicado via data-bs-theme
    const antiFlash = document.getElementById('anti-flash-theme')
    if (antiFlash) antiFlash.remove()
    window.addEventListener('resize', this.handleResize)
    this.handleResize()
    // Verifica se deve abrir um módulo específico (vindo de outra aba via sessionStorage)
    const moduloPendente = sessionStorage.getItem('sugestoesAbrirModulo')
    if (moduloPendente) {
      sessionStorage.removeItem('sugestoesAbrirModulo')
      this.$nextTick(() => {
        this.showModule(moduloPendente, 'Sugestões de Melhorias')
      })
    }
    // Pendencias: abrir módulo vindo de outra aba (Ctrl+clique no sino)
    const pendenciaDestino = sessionStorage.getItem('pendenciasDestino')
    if (pendenciaDestino) {
      sessionStorage.removeItem('pendenciasDestino')
      try {
        const destino = JSON.parse(pendenciaDestino)
        this.$nextTick(() => {
          this.showModule(destino.modulo, destino.titulo || destino.modulo)
          this.abrirItemPendencia(destino)
        })
      } catch (e) { /* ignora JSON inválido */ }
    }
    const dropdownElement = this.$refs.notificationsHistoryButton
    if (dropdownElement) {
      dropdownElement.addEventListener('shown.bs.dropdown', () => {
        this.isNotificationHistoryOpen = true
      })
      dropdownElement.addEventListener('hidden.bs.dropdown', () => {
        this.isNotificationHistoryOpen = false
      })
    }
    document.addEventListener('click', this.handleClickOutside)
    if (this.isAdminInterno) {
      this.buscarEmpresasParaSelecao()
    }

    // Carrega as organizações do tenant ativo (multi-organização)
    this.carregarOrganizacoes()

    // Instancia o composable useMenuBackend para carregar o menu da API
    const menuBackendInstance = useMenuBackend()
    this.menuBackend = menuBackendInstance

    // Carrega o menu da API (fallback silencioso para menuConfig hardcoded)
    // NOTA: o await garante que o dicionarioPorChave esteja populado antes
    // de tentar resolver a navegação via ?regra=CHAVE
    await menuBackendInstance.carregarMenu()
    if (menuBackendInstance.menuFromApi.value && menuBackendInstance.menuFromApi.value.length > 0) {
      // Substitui o menuConfig hardcoded pelo vindo da API
      this.menuConfig = menuBackendInstance.menuFromApi.value
      this.menuPermissaoInstance = useMenuPermissao(this.menuConfig)
      console.log('[MainDashboard] Menu carregado da API:', this.menuConfig.length, 'grupos')
    }

    // Navegação via query params — suporta ?regra=CHAVE (novo) e ?modulo=Nome (legado)
    const queryRegra = this.$route?.query?.regra
    const queryModulo = this.$route?.query?.modulo
    const queryAcao = this.$route?.query?.acao

    if (queryRegra) {
      const resolvido = this.menuBackend.resolver(queryRegra)
      if (resolvido) {
        this.$nextTick(() => {
          this.selecionarModulo(resolvido.telaAbertura, resolvido.titulo, resolvido.permissoes, resolvido.extraProps)
          if (queryAcao === 'adicionar') {
            this.abrirPainelFormulario(null)
          }
        })
      }
    } else if (queryModulo) {
      this.$nextTick(() => {
        this.selecionarModulo(queryModulo, queryModulo.replace('Administrar', '').replace(/([A-Z])/g, ' $1').trim())
        if (queryAcao === 'adicionar') {
          this.abrirPainelFormulario(null)
        }
      })
    }

    // Instancia o composable uma única vez (evita recriação a cada chamada)
    const favoritosComposable = useFavoritos()
    console.log('[MainDashboard mounted] useFavoritos retornou keys:', Object.keys(favoritosComposable), '| favoritos type:', typeof favoritosComposable.favoritos, '| favoritos value:', JSON.stringify(favoritosComposable.favoritos?.value))
    this.favoritosInstance = {
      carregarFavoritos: favoritosComposable.carregarFavoritos,
      salvarFavoritos: favoritosComposable.salvarFavoritos,
      favoritos: favoritosComposable.favoritos
    }

    // Carregar favoritos ao iniciar
    this.carregarFavoritos()

    // Atalho de teclado Ctrl+K para focar na busca
    document.addEventListener('keydown', (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault()
        this.expandirBusca()
      }
    })
  },
  beforeUnmount () {
    document.removeEventListener('click', this.handleClickOutside)
  }
}
</script>

<style>
#app-layout { display: flex; flex-direction: column; min-height: 100vh; }
.fixed-top, .fixed-bottom { position: fixed; left: 0; right: 0; z-index: 11000; }
.fixed-bottom { z-index: 11000; }
.fixed-top { top: 0; }
.fixed-bottom { bottom: 0; }
.pt-5 { padding-top: 56px !important; }
.content-area { flex-grow: 1; display: grid; position: relative; padding: 0; z-index: 1000; grid-template-columns: 1fr 0px 0fr; transition: grid-template-columns 0.35s cubic-bezier(0.2, 0.8, 0.2, 1); }
.content-area.panel-is-open { grid-template-columns: 0fr 21px 1fr; }
.tracking-wide { letter-spacing: 0.05em; }
.tracking-wider { letter-spacing: 0.08em; }

.sidebar {
  height: 100%;
  box-shadow: 1px 0 10px rgba(0,0,0,0.05);
  transition: width 0.3s ease, min-width 0.3s ease, transform 0.3s ease, background-color 0.3s ease;
  flex-shrink: 0;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  padding-top: 0 !important;
  position: relative;
  z-index: 1040;
  background-color: var(--bs-body-bg);
  border-right: 1px solid var(--bs-border-color-translucent);
}

/*
 * Lista de menus do sidebar: ocupa apenas o espaço restante (após o bloco
 * de busca/toggle). Sem altura fixa (h-100) e com min-height: 0 para que,
 * em container flex-column, ela não estoure a altura do aside e "vaze"
 * atrás do header/footer.
 */
.sidebar-menu-list {
  flex: 1 1 auto;
  min-height: 0;
}
.sidebar-expanded-mode { width: 240px !important; min-width: 240px !important; }
.sidebar-collapsed-mode { width: 70px !important; min-width: 70px !important; text-align: center; }
.sidebar-hidden { margin-left: -240px !important; width: 0 !important; min-width: 0 !important; }

/* ---------------------------------------------------
   VISUAL CLÁSSICO E SÓBRIO DO MENU (Cinza, Sem Negrito, Sem Azul)
   --------------------------------------------------- */
.sidebar .nav-link,
.sidebar .group-link,
.sidebar .submenu-link,
.flyout-link {
  color: #495057 !important;
  font-weight: 400 !important;
  font-size: 1rem !important;
  white-space: nowrap;
  transition: all 0.2s ease;
  text-transform: none !important;
  letter-spacing: normal !important;
  text-decoration: none;
}

/* Exceção garantida para o menu Inicialização (Vermelho) */
.sidebar .nav-link.text-danger,
.sidebar .submenu-link.text-danger,
.flyout-link.text-danger {
  color: #dc3545 !important;
}

[data-bs-theme="dark"] .sidebar .nav-link:not(.text-danger),
[data-bs-theme="dark"] .sidebar .group-link,
[data-bs-theme="dark"] .sidebar .submenu-link:not(.text-danger),
[data-bs-theme="dark"] .flyout-link:not(.text-danger) {
  color: #adb5bd !important;
}

/* Ícones do Menu: Tamanho Clássico e Cor Herdada */
.sidebar .nav-link i,
.flyout-link i {
  color: #495057 !important;
  font-size: 1.1rem !important;
  margin-right: 0.5rem;
}

/* Exceção do ícone vermelho para a Inicialização */
.sidebar .nav-link.text-danger i,
.flyout-link.text-danger i {
  color: #dc3545 !important;
}

[data-bs-theme="dark"] .sidebar .nav-link:not(.text-danger) i,
[data-bs-theme="dark"] .flyout-link:not(.text-danger) i {
  color: #adb5bd !important;
}

/* Espaçamentos e Estrutura Clássica */
.group-link {
  padding: 0.5rem 1rem 0.5rem 1.5rem !important;
  margin: 0 0.5rem !important;
  border-radius: 0.25rem !important;
  border-left: 3px solid transparent !important;
}

.sidebar-collapsed-mode .group-link {
  justify-content: center !important;
  padding: 0.5rem 0.9rem !important;
  border-radius: 0.25rem !important;
  margin: 0 auto !important;
}

.sidebar-collapsed-mode .group-link i {
  margin-right: 0 !important;
}

.sidebar-collapsed-mode .group-link span {
  display: none !important;
}

.sidebar-collapsed-mode .text-center.py-2 .btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0 !important;
  margin: 2px auto;
}

/* ---------------------------------------------------
   O HOVER: Cinza Sutil
   --------------------------------------------------- */
.sidebar .nav-link:hover,
.sidebar .group-link.flyout-open,
.flyout-link:hover {
  background-color: var(--bs-secondary-bg) !important;
  color: var(--bs-body-emphasis) !important;
  border-left-color: #6c757d !important;
}

[data-bs-theme="dark"] .sidebar .nav-link:hover,
[data-bs-theme="dark"] .sidebar .group-link.flyout-open,
[data-bs-theme="dark"] .flyout-link:hover {
  border-left-color: #adb5bd !important;
}

.sidebar .nav-link:hover i,
.sidebar .group-link.flyout-open i,
.flyout-link:hover i {
  color: var(--bs-body-emphasis) !important;
}

/* ---------------------------------------------------
   O ATIVO (Sub-links selecionados): Fundo Cinza/Sólido (Sem Azul!)
   --------------------------------------------------- */
.sidebar .nav-link.active,
.flyout-link.active {
  background-color: #e9ecef !important;
  color: #212529 !important;
  font-weight: 500 !important;
  border-radius: 0.25rem !important;
}

[data-bs-theme="dark"] .sidebar .nav-link.active,
[data-bs-theme="dark"] .flyout-link.active {
  background-color: #343a40 !important;
  color: #f8f9fa !important;
}

[data-bs-theme="dark"] .sidebar .nav-link.active i,
[data-bs-theme="dark"] .flyout-link.active i {
  color: #f8f9fa !important;
}

/* ---------------------------------------------------
   O MENU FLUTUANTE (FLYOUT)
   --------------------------------------------------- */
.sidebar-flyout {
  position: fixed;
  z-index: 1060;
  background-color: var(--bs-body-bg);
  border: 1px solid var(--bs-border-color-translucent);
  border-radius: 0.5rem;
  width: 260px;
  padding: 0.5rem 0;
  box-shadow: 0 10px 30px rgba(0,0,0,0.12);
  max-height: calc(100vh - 56px - 40px - 16px);
  overflow-y: auto;
}

.sidebar-flyout::before {
  content: ""; position: absolute;
  top: 0; left: -20px; width: 20px; height: 100%;
}
.flyout-header {
  padding: 0.5rem 1.5rem;
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--bs-secondary-color);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.2rem;
  border-bottom: 1px solid var(--bs-border-color-translucent);
}
.flyout-item {
  margin: 0.1rem 0.5rem;
}
.flyout-link {
  padding: 0.6rem 1rem !important;
}

/* Acordeões Mobile */
.mobile-submenu {
  border-top: 1px solid var(--bs-border-color-translucent);
  border-bottom: 1px solid var(--bs-border-color-translucent);
}
.submenu-link {
  padding-left: 3rem !important;
}

/* Resultados da busca no sidebar: padding alinhado com group-link, não com submenu-link */
.search-result-link {
  padding: 0.5rem 1rem 0.5rem 1.5rem !important;
  margin: 0 0.5rem !important;
  border-radius: 0.25rem !important;
  white-space: normal;
}
.search-result-link i {
  font-size: 1.1rem !important;
  margin-right: 0.5rem;
  color: #495057 !important;
}
[data-bs-theme="dark"] .search-result-link i {
  color: #adb5bd !important;
}
.search-result-link:hover {
  background-color: var(--bs-secondary-bg) !important;
  color: var(--bs-body-emphasis) !important;
}
.search-result-link.active {
  background-color: #e9ecef !important;
  color: #212529 !important;
  font-weight: 500 !important;
}
[data-bs-theme="dark"] .search-result-link.active {
  background-color: #343a40 !important;
  color: #f8f9fa !important;
}
.sidebar-collapsed-mode .search-result-link {
  justify-content: center !important;
  padding: 0.5rem 0.9rem !important;
  margin: 0 auto !important;
}
.sidebar-collapsed-mode .search-result-link i {
  margin-right: 0 !important;
}

/* ---------------------------------------------------
   ÁREA DE CONTEÚDO E DIVISOR
   --------------------------------------------------- */
/*
 * GUARDIÃO: o container de scroll NÃO pode ter padding-top.
 * Um header `position: sticky; top: 0` filho do conteúdo cola no topo da
 * content-box do scrollport; com padding-top no container, o header fica
 * preso ~padding abaixo do topo e deixa uma faixa onde o conteúdo (linhas do
 * grid) "vaza" por trás do header ao rolar. Sem padding-top, o header cola
 * exatamente no topo do scrollport — mesmo comportamento do FormularioCadastro
 * (cujo container `.admin-panel-body` já usa padding: 0).
 * Importante: NÃO reintroduzir `padding-top` aqui.
 */
.list-container { grid-column: 1; overflow-y: auto; padding: 0 1.5rem 1.5rem; }
.list-container.list-collapsed { padding: 0; }

.divider {
  grid-column: 2; width: 21px; cursor: pointer;
  background-color: var(--bs-tertiary-bg);
  border-left: 1px solid var(--bs-border-color-translucent);
  border-right: 1px solid var(--bs-border-color-translucent);
  display: flex; align-items: center; justify-content: center; flex-direction: column;
  z-index: 1025; color: var(--bs-body-color);
  transition: background-color 0.2s ease, color 0.2s ease;
}
.divider:hover { background-color: var(--bs-secondary-bg); color: var(--bs-body-emphasis); }
.divider-text { transform: rotate(-180deg); white-space: nowrap; writing-mode: vertical-lr; font-size: 1rem; margin-bottom: 5px; }
.divider-icon { font-size: 2rem; }

.admin-panel { grid-column: 3; height: 100%; box-shadow: -5px 0 20px rgba(0,0,0,0.08); display: flex; flex-direction: column; z-index: 1020; overflow: hidden; }
.admin-panel-body { padding: 0; overflow-y: auto; flex-grow: 1; min-height: 0; background-color: var(--bs-body-bg); transition: background-color 0.3s ease; }

@media (max-width: 991.98px) {
  /* Previne scroll horizontal causado pelo sidebar */
  body {
    overflow-x: hidden;
  }

  /* Ajuste da altura principal: footer some, então main-body ocupa até o fim */
  .main-body {
    height: calc(100vh - 56px) !important;
  }

  .sidebar {
    position: fixed;
    height: calc(100vh - 56px);
    top: 56px;
    left: 0;
    width: 260px !important;
    min-width: 260px !important;
    margin-left: 0 !important;
    transform: translateX(0);
    z-index: 1050;
  }
  .sidebar-hidden {
    width: 260px !important;
    min-width: 260px !important;
    margin-left: 0 !important;
    transform: translateX(-100%);
  }

  .content-area { grid-template-columns: 1fr !important; margin-left: 0 !important; }
  .content-area.panel-is-open { grid-template-columns: 0fr 0px 1fr !important; }
  .content-area.panel-is-open .divider { display: none; }

  /* Espaço para a Bottom Tab Bar no mobile (56px) */
  .content-area.has-mobile-nav {
    padding-bottom: 56px;
  }

  /* Footer oculto no mobile — o toast flutuante substitui */
  footer.fixed-bottom {
    display: none !important;
  }
}

.sidebar-mobile-backdrop {
  position: fixed;
  top: 56px;
  left: 0;
  width: 100vw;
  height: calc(100vh - 56px);
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1045;
  backdrop-filter: blur(2px);
}

.notification-badge { padding: 0.35rem 1rem; border-radius: 2rem; font-weight: 500; display: inline-flex; align-items: center; font-size: 0.85rem; color: #fff; box-shadow: 0 2px 8px rgba(0,0,0,0.2); }
.notification-badge i { color: #fff !important; }
.notification-badge-success { background-color: #198754; }
.notification-badge-error { background-color: #dc3545; }
.notification-badge-warning { background-color: #ffc107; color: #000; }
.notification-badge-warning i { color: #000 !important; }
.notification-badge-info { background-color: #0dcaf0; color: #000; }
.notification-badge-info i { color: #000 !important; }
.notification-area { min-height: 20px; text-align: right; flex-grow: 1; }
.dropup .dropdown-menu { margin-bottom: 0.5rem; }
.dropdown-menu-dark .dropdown-item-text { color: #f8f9fa; }
.history-item-text { font-size: 0.75rem !important; white-space: normal; }
/*
 * Área útil entre o header fixo (56px) e o footer fixo (40px).
 * - margin-top empurra o bloco para baixo do header (não usar padding,
 *   que deslocaria o conteúdo mas manteria o topo atrás do header).
 * - height calculada exclui header + footer.
 * - Sem flex-grow-1: o pai (#app-layout) tem min-height: 100vh e, sendo
 *   este o único filho em fluxo, o grow esticava o bloco até 100vh,
 *   fazendo o sidebar vazar por trás do footer.
 */
.main-body {
  height: calc(100vh - 56px - 40px);
  margin-top: 56px;
  overflow: hidden;
}
footer.fixed-bottom { height: 40px; z-index: 11000; border-top: 1px solid #1f2d3a; }
header.fixed-top { z-index: 11000; }
.admin-header-global {
  background-color: #34495E;
  border-bottom: 1px solid #1f2d3a;
  /*
   * Escala FIXA de primeiro plano para o chrome do header.
   * O header tem cor de fundo fixa (não reage ao data-bs-theme), então o
   * primeiro plano também deve ser fixo — nunca usar tokens semânticos do
   * Bootstrap (*-subtle/*-emphasis/text-white-50) nem brancos de baixa opacidade.
   */
  --hdr-fg-strong: #FFFFFF;
  --hdr-fg-regular: #E6EAEE;
  --hdr-fg-muted: #C7D0DA;
  --hdr-fg-disabled: #9AA6B2;
  --hdr-accent-ia: #22D3EE;
  --hdr-accent-sugestoes: #8B5CF6;
  color: var(--hdr-fg-regular);
}

/* --- Bloco central do header: Helper (IA) + Sugestões e Dúvidas (par ghost) --- */
.header-center-actions {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  z-index: 2;
}
.header-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  height: 32px;
  padding: 0 0.75rem;
  font-size: 0.82rem;
  font-weight: 500;
  line-height: 1;
  border-radius: 0.5rem;
  box-shadow: none;
  transition: background-color 0.25s ease, border-color 0.25s ease, color 0.25s ease, box-shadow 0.25s ease, transform 0.25s ease;
}
/*
 * Vence o .btn do Bootstrap (que define color: var(--bs-btn-color) = --bs-body-color,
 * escuro no tema claro). Aqui fixamos as cores do botão para a superfície fixa do header.
 */
.header-action-btn.btn {
  --bs-btn-color: var(--hdr-fg-regular);
  --bs-btn-bg: transparent;
  --bs-btn-border-color: transparent;
  --bs-btn-hover-color: var(--hdr-fg-strong);
  --bs-btn-hover-bg: rgba(255, 255, 255, 0.08);
  --bs-btn-hover-border-color: rgba(255, 255, 255, 0.18);
  --bs-btn-active-color: var(--hdr-fg-strong);
  --bs-btn-active-bg: rgba(255, 255, 255, 0.12);
  --bs-btn-active-border-color: rgba(255, 255, 255, 0.24);
  --bs-btn-focus-shadow-rgb: 255, 255, 255;
  color: var(--hdr-fg-regular);
}
.header-action-btn.btn:hover,
.header-action-btn.btn:focus-visible {
  color: var(--hdr-fg-strong);
}
/*
 * Hover temático: cada botão acende a PRÓPRIA borda colorida e ganha uma leve
 * elevação. A suavidade vem da `transition` declarada em .header-action-btn.
 */
.header-action-helper.btn {
  --bs-btn-hover-bg: rgba(34, 211, 238, 0.12);
  --bs-btn-hover-border-color: var(--hdr-accent-ia);
  --bs-btn-active-bg: rgba(34, 211, 238, 0.18);
  --bs-btn-active-border-color: var(--hdr-accent-ia);
}
.header-action-sugestoes.btn {
  --bs-btn-hover-bg: rgba(139, 92, 246, 0.14);
  --bs-btn-hover-border-color: var(--hdr-accent-sugestoes);
  --bs-btn-active-bg: rgba(139, 92, 246, 0.2);
  --bs-btn-active-border-color: var(--hdr-accent-sugestoes);
}
.header-action-helper.btn:hover,
.header-action-helper.btn:focus-visible {
  box-shadow: 0 0 0 3px rgba(34, 211, 238, 0.16);
  transform: translateY(-1px);
}
.header-action-sugestoes.btn:hover,
.header-action-sugestoes.btn:focus-visible {
  box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.2);
  transform: translateY(-1px);
}
/* Ícones: identidade carregada só pela cor; o bulb (lightbulb-fill) preenche
   menos o em-box que o robot, então recebe um font-size maior para igualar a
   ALTURA visual dos dois ícones (e do badge "AI"). */
.header-action-btn i {
  line-height: 1;
}
.header-action-helper i {
  color: var(--hdr-accent-ia);
  font-size: 1.2rem;
  /* O glifo do robot encosta visualmente no texto — afasta um pouco mais */
  margin-right: 0.3rem;
}
/* Só no Helper: afasta a badge "AI" do label */
.header-action-helper .header-action-badge-ai {
  margin-left: 0.35rem;
}
.header-action-sugestoes i {
  color: var(--hdr-accent-sugestoes);
  font-size: 1.4rem;
}
/* Badge "AI": mesma ALTURA dos ícones (fill sólido + texto escuro) */
.header-action-badge-ai {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 1.2rem;
  padding: 0 0.45rem;
  font-size: 0.62rem;
  font-weight: 700;
  line-height: 1;
  border: none;
  border-radius: 0.35rem;
  background-color: var(--hdr-accent-ia);
  color: #083344;
}
.content-area, .admin-panel { height: 100%; }
.blinking-icon { animation: blink-animation 0.2s steps(1, start) infinite; }
@keyframes blink-animation { from { opacity: 1; } to { opacity: 0; } }

/* --- Histórico de Notificações: Largura --- */
.notification-history-menu {
  max-height: 40vh;
  overflow-y: auto;
  min-width: 420px !important;
  max-width: 600px !important;
}
</style>
